import React, { useState, useEffect } from 'react';
import {
  Stack,
  Text,
  IconButton,
  CommandBar,
  ICommandBarItemProps,
  SearchBox,
  Dropdown,
  IDropdownOption,
  DetailsList,
  IColumn,
  SelectionMode,
  Selection,
  Panel,
  PanelType,
  PrimaryButton,
  DefaultButton,
  TextField,
  Toggle,
  Spinner,
  SpinnerSize,
  MessageBar,
  MessageBarType,
  Modal,
  DatePicker,
  TimePicker,
  Checkbox,
  Label,
  ProgressIndicator,
  Separator,
  getTheme
} from '@fluentui/react';
import {
  AddRegular,
  EditRegular,
  DeleteRegular,
  PlayRegular,
  PauseRegular,
  StopRegular,
  CalendarRegular,
  CopyRegular,
  FilterRegular,
  MoreHorizontalRegular,
  DatabaseRegular,
  ConnectorRegular,
  SettingsRegular,
  EyeRegular,
  ShareIosRegular,
  FolderRegular,
  GlobeRegular,
  CloudRegular,
  BuildingRegular,
  ChartMultipleRegular,
  PersonRegular,
  MailRegular,
  CheckmarkCircleRegular,
  ErrorCircleRegular,
  WarningRegular,
  ClockRegular,
  ArrowClockwiseRegular,
  ArrowRedoRegular,
  TextAlignLeftRegular
} from '@fluentui/react-icons';
import { authenticatedFetch } from '../../utils/authApi';
import styles from './JobManagement.module.css';

// Icons are registered centrally in utils/iconRegistry.ts

interface JobManagementProps {
  onNavigateToConnectors?: () => void;
}

interface LogEntry {
  timestamp: string;
  level: string;
  message: string;
}

interface Job {
  id: string;
  name?: string;
  job_name?: string; // Alternative field name from backend
  description?: string;
  connectorType?: string;
  connector_type?: any; // Alternative field structure from backend
  connectorTemplate?: string;
  targetIndex?: string;
  index_name?: string; // Alternative field name from backend
  status: 'created' | 'active' | 'running' | 'paused' | 'disabled' | 'error' | 'scheduled' | 'completed' | 'failed' | 'cancelled' | 'stopped'; // Status types
  lastRun?: string;
  created_at?: string; // Alternative field name from backend
  nextRun?: string;
  runCount?: number;
  successRate?: number;
  documentsProcessed?: number;
  documentsDiscovered?: number;
  documentsIndexed?: number;
  documentsFailed?: number;
  avgRunTime?: number;
  created?: string;
  createdBy?: string;
  crawl_mode?: 'full' | 'delta';
  lastFullCrawl?: string;
  last_crawl_timestamp?: string;
  schedule?: {
    enabled: boolean;
    frequency: 'daily' | 'weekly' | 'monthly' | 'hourly';
    time: string;
    dayOfWeek?: number;
    dayOfMonth?: number;
    timezone: string;
  };
  configuration?: Record<string, any>;
  config?: any; // Alternative field structure from backend
  emailFilters?: {
    enabled: boolean;
    domains: string[];
    subjects: string[];
    fromAddresses: string[];
    toAddresses: string[];
    excludePatterns: string[];
  };
  progress?: {
    current_item: string;
    items_processed: number;
    phase: string;
    total_items: number;
  };
  completion_summary?: {
    documents_discovered?: number;
    documents_processed?: number;
    documents_indexed?: number;
    documents_failed?: number;
    chunks_created?: number;
    chunks_indexed?: number;
    acl_entries?: number;
    processing_errors?: number;
    processing_duration?: string;
    total_documents_found?: number;
    success_rate?: string;
    discovered_entities?: Record<string, string[]>;
    // Office 365 specific fields
    emails_processed?: number;
    emails_indexed?: number;
    emails_filtered?: number;
    drive_items_processed?: number;
    drive_items_indexed?: number;
    attachments_processed?: number;
    total_documents_indexed?: number;
    targets_processed?: number;
    entities_extracted?: number;
    errors?: number;
    crawl_completed?: boolean;
    crawl_timestamp?: string;
  };
  discoveredEntities?: Record<string, Array<{name: string, count: number, confidence: number}>>;
}

const JobManagement: React.FC<JobManagementProps> = ({ onNavigateToConnectors }) => {
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [filteredJobs, setFilteredJobs] = useState<Job[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [connectorFilter, setConnectorFilter] = useState<string>('all');
  const [showJobPanel, setShowJobPanel] = useState(false);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [jobToDelete, setJobToDelete] = useState<Job | null>(null);
  const [showLogsPanel, setShowLogsPanel] = useState(false);
  const [showJobDetailsPanel, setShowJobDetailsPanel] = useState(false);
  const [jobLogs, setJobLogs] = useState<string | LogEntry[]>('');
  const [logsLoading, setLogsLoading] = useState(false);
  const [showEditJobPanel, setShowEditJobPanel] = useState(false);
  const [editingJob, setEditingJob] = useState<Job | null>(null);
  const [editFormData, setEditFormData] = useState<any>({});
  const [editLoading, setEditLoading] = useState(false);
  const [selectedJobs, setSelectedJobs] = useState<Set<string>>(new Set());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [availableIndices, setAvailableIndices] = useState<string[]>([]);
  const [indexValidationError, setIndexValidationError] = useState<string>('');

  // Index name validation function
  const validateIndexName = (indexName: string): string => {
    if (!indexName) {
      return 'Index name is required';
    }
    if (indexName !== indexName.toLowerCase()) {
      return 'Index name must be lowercase';
    }
    if (indexName.includes(' ')) {
      return 'Index name cannot contain spaces';
    }
    if (!/^[a-z0-9][a-z0-9-]*[a-z0-9]$/.test(indexName) && indexName.length > 1) {
      return 'Index name must start and end with alphanumeric characters, and can contain hyphens';
    }
    if (indexName.length < 2) {
      return 'Index name must be at least 2 characters long';
    }
    return '';
  };

  // Load available indices
  const loadAvailableIndices = async () => {
    try {
      const response = await authenticatedFetch('/api/connector-hub/indices');
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.indices) {
          setAvailableIndices(result.indices);
        }
      }
    } catch (error) {
      console.error('Error loading available indices:', error);
    }
  };

  // Custom selection handlers
  const toggleJobSelection = (jobId: string) => {
    const newSelected = new Set(selectedJobs);
    if (newSelected.has(jobId)) {
      newSelected.delete(jobId);
    } else {
      newSelected.add(jobId);
    }
    setSelectedJobs(newSelected);
  };

  const selectAllJobs = () => {
    if (selectedJobs.size === filteredJobs.length) {
      setSelectedJobs(new Set()); // Deselect all
    } else {
      setSelectedJobs(new Set(filteredJobs.map(job => job.id))); // Select all
    }
  };

  const clearSelection = () => {
    setSelectedJobs(new Set());
  };

  // Helper function to normalize job data from different backend response formats
  const normalizeJob = (job: any): Job => {
    return {
      id: job.id || '',
      name: job.name || job.job_name || job.config?.job_name || '',
      description: job.description || '',
      connectorType: job.connectorType || job.connector_type?.type || job.connector_type?.id || job.config?.connector_type?.type || '',
      connectorTemplate: job.connectorTemplate || job.connector_type?.id || '',
      targetIndex: job.targetIndex || job.index_name || job.config?.index_name || '',
      status: job.status || 'created',
      lastRun: job.lastRun || job.created_at || '',
      nextRun: job.nextRun,
      runCount: job.runCount || 0,
      successRate: job.successRate || 0,
      documentsProcessed: job.documentsProcessed || 0,
      avgRunTime: job.avgRunTime || 0,
      created: job.created || job.created_at || '',
      createdBy: job.createdBy || 'user',
      crawl_mode: job.crawl_mode || 'full',
      lastFullCrawl: job.lastFullCrawl || job.last_full_crawl,
      last_crawl_timestamp: job.last_crawl_timestamp,
      schedule: job.schedule,
      configuration: job.configuration || job.config?.configuration || {},
      emailFilters: job.emailFilters,
      progress: job.progress,
      completion_summary: job.completion_summary || job.completionSummary || {},
      discoveredEntities: job.discoveredEntities || job.discovered_entities || {}
    };
  };

  // Helper function to get job display name
  const getJobDisplayName = (job: Job): string => {
    return job.name || job.job_name || 'Unnamed Job';
  };

  // Helper function to get job connector type display name
  const getJobConnectorType = (job: Job): string => {
    const type = job.connectorType || job.connector_type?.type || job.connector_type?.id || '';
    return String(type).replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  useEffect(() => {
    loadJobManagementData();
    loadAvailableIndices();
  }, []);

  useEffect(() => {
    filterJobs();
  }, [jobs, searchQuery, statusFilter, connectorFilter]);

  // Light polling for running jobs to show progress (disabled to prevent page refresh)
  useEffect(() => {
    const hasRunningJobs = jobs.some(job => job.status === 'running');
    
    if (hasRunningJobs) {
      // Disabled automatic polling to prevent page refresh behavior
      // Users can manually refresh to see progress updates
      // const pollInterval = setInterval(() => {
      //   loadJobManagementDataSilent(); // Use silent version without loading spinner
      // }, 10000); // Reduced frequency to 10 seconds
      
      // return () => clearInterval(pollInterval);
    }
  }, [jobs]);

  const loadJobManagementData = async () => {
    try {
      setLoading(true);
      
      const jobsResponse = await authenticatedFetch('/api/connector-hub/jobs');
      const jobsResult = await jobsResponse.json();

      console.log('API Results:', { jobsResult });

      // Use real API data only, no fallbacks
      if (jobsResult && jobsResult.success && Array.isArray(jobsResult.jobs)) {
        console.log('Using real jobs data from API:', jobsResult.jobs);
        
        // Normalize jobs and fetch crawl info for each
        const normalizedJobs = await Promise.all(
          jobsResult.jobs.map(async (job: any) => {
            const normalized = normalizeJob(job);
            
            // Fetch crawl info if job is completed or running
            if (job.status === 'completed' || job.status === 'running' || job.status === 'active' || job.status === 'cancelled' || job.status === 'stopped') {
              try {
                const crawlInfoResponse = await authenticatedFetch(`/api/connector-hub/jobs/${job.id}/crawl-info`);
                const crawlInfo = await crawlInfoResponse.json();
                
                if (crawlInfo.success) {
                  normalized.crawl_mode = crawlInfo.crawl_mode;
                  normalized.lastFullCrawl = crawlInfo.lastFullCrawl;
                  normalized.last_crawl_timestamp = crawlInfo.last_crawl_timestamp;
                }
              } catch (error) {
                console.log(`Failed to fetch crawl info for job ${job.id}:`, error);
              }
            }
            
            return normalized;
          })
        );
        
        setJobs(normalizedJobs);
      } else {
        console.log('Failed to load real jobs data, using empty array');
        setJobs([]);
      }

    } catch (error) {
      console.error('Error loading job management data:', error);
      
      // Use empty arrays only, no mock data fallbacks
      console.log('Failed to load data, using empty arrays');
      setJobs([]);
    } finally {
      // Always set loading to false
      setLoading(false);
    }
  };

  // Silent version of loadJobManagementData for background updates (no loading spinner)
  const loadJobManagementDataSilent = async () => {
    try {
      const jobsResponse = await authenticatedFetch('/api/connector-hub/jobs');
      const jobsResult = await jobsResponse.json();

      console.log('Silent API update:', { jobsResult });

      // Use real API data only, no fallbacks
      if (jobsResult && jobsResult.success && Array.isArray(jobsResult.jobs)) {
        console.log('Silent update using real jobs data from API:', jobsResult.jobs);
        
        // Normalize jobs and fetch crawl info for each
        const normalizedJobs = await Promise.all(
          jobsResult.jobs.map(async (job: any) => {
            const normalized = normalizeJob(job);
            
            // Fetch crawl info if job is completed or running
            if (job.status === 'completed' || job.status === 'running' || job.status === 'active' || job.status === 'cancelled' || job.status === 'stopped') {
              try {
                const crawlInfoResponse = await authenticatedFetch(`/api/connector-hub/jobs/${job.id}/crawl-info`);
                const crawlInfo = await crawlInfoResponse.json();
                
                if (crawlInfo.success) {
                  normalized.crawl_mode = crawlInfo.crawl_mode;
                  normalized.lastFullCrawl = crawlInfo.lastFullCrawl;
                  normalized.last_crawl_timestamp = crawlInfo.last_crawl_timestamp;
                }
              } catch (error) {
                console.log(`Failed to fetch crawl info for job ${job.id}:`, error);
              }
            }
            
            return normalized;
          })
        );
        
        setJobs(normalizedJobs);
      } else {
        console.log('Silent update: Failed to load real jobs data');
      }

    } catch (error) {
      console.error('Error in silent job management data update:', error);
    }
  };

  const filterJobs = () => {
    let filtered = jobs;

    if (searchQuery) {
      filtered = filtered.filter(job => {
        const name = getJobDisplayName(job);
        const description = job.description || '';
        const connectorType = getJobConnectorType(job);
        return name.toLowerCase().includes(searchQuery.toLowerCase()) ||
               description.toLowerCase().includes(searchQuery.toLowerCase()) ||
               connectorType.toLowerCase().includes(searchQuery.toLowerCase());
      });
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(job => job.status === statusFilter);
    }

    if (connectorFilter !== 'all') {
      filtered = filtered.filter(job => job.connectorType === connectorFilter);
    }

    setFilteredJobs(filtered);
  };

  const renderJobsList = () => {
    const jobColumns: IColumn[] = [
      {
        key: 'checkbox',
        name: '',
        minWidth: 48,
        maxWidth: 48,
        onRenderHeader: () => (
          <Checkbox
            checked={selectedJobs.size === filteredJobs.length && filteredJobs.length > 0}
            indeterminate={selectedJobs.size > 0 && selectedJobs.size < filteredJobs.length}
            onChange={() => selectAllJobs()}
            title={selectedJobs.size === filteredJobs.length && filteredJobs.length > 0 ? 'Deselect all' : 'Select all'}
          />
        ),
        onRender: (item: Job) => (
          <Checkbox
            checked={selectedJobs.has(item.id)}
            onChange={() => toggleJobSelection(item.id)}
            title={`${selectedJobs.has(item.id) ? 'Deselect' : 'Select'} job`}
          />
        )
      },
      {
        key: 'name',
        name: 'Job Name',
        fieldName: 'name',
        minWidth: 200,
        maxWidth: 300,
        onRender: (item: Job) => (
          <Stack>
            <Text variant="medium" style={{ fontWeight: 500 }}>{getJobDisplayName(item)}</Text>
            <Text variant="small" style={{ color: '#666' }}>
              {item.description || ''}
            </Text>
          </Stack>
        )
      },
      {
        key: 'connector',
        name: 'Connector',
        minWidth: 120,
        maxWidth: 150,
        onRender: (item: Job) => (
          <Stack tokens={{ childrenGap: 4 }}>
            <Text variant="small" style={{ fontWeight: 500 }}>
              {getJobConnectorType(item)}
            </Text>
            <Text variant="small" style={{ color: '#666' }}>
              → {item.targetIndex || item.index_name || 'Unknown Index'}
            </Text>
          </Stack>
        )
      },
      {
        key: 'status',
        name: 'Status',
        minWidth: 100,
        maxWidth: 120,
        onRender: (item: Job) => (
          <Stack tokens={{ childrenGap: 2 }}>
            <div className={`${styles.statusBadge} ${styles[item.status as string]}`}>
              {item.status}
            </div>
            {(item.status === 'running' || item.status === 'active') && item.crawl_mode && (
              <Text variant="small" style={{ color: '#666', fontSize: '10px' }}>
                {item.crawl_mode === 'delta' ? '🔄 Delta' : '🔄 Full'} Crawl
              </Text>
            )}
          </Stack>
        )
      },
      {
        key: 'schedule',
        name: 'Schedule',
        minWidth: 120,
        maxWidth: 150,
        onRender: (item: Job) => (
          <Stack tokens={{ childrenGap: 2 }}>
            {item.schedule?.enabled ? (
              <>
                <Text variant="small" style={{ fontWeight: 500 }}>
                  {item.schedule.frequency} at {item.schedule.time}
                </Text>
                {item.nextRun && (
                  <Text variant="small" style={{ color: '#666' }}>
                    Next: {new Date(item.nextRun).toLocaleDateString()}
                  </Text>
                )}
              </>
            ) : (
              <Text variant="small" style={{ color: '#666' }}>Manual only</Text>
            )}
          </Stack>
        )
      },
      {
        key: 'performance',
        name: 'Performance',
        minWidth: 160,
        maxWidth: 200,
        onRender: (item: Job) => {
          const progress = item.progress;
          let progressPercentage = 0;
          let docsProcessed = 0;
          let totalDocs = 0;
          let displayText = 'docs processed';

          // Use the most reliable progress data available
          // Priority: documentsProcessed > progress.items_processed for actual progress
          docsProcessed = item.documentsProcessed || progress?.items_processed || 0;
          
          // Special handling for delta crawls
          const isDeltaCrawl = item.crawl_mode === 'delta';
          
          if (progress) {
            // Use progress.total_items as the primary source for total documents
            totalDocs = progress.total_items || item.documentsDiscovered || 0;
            
            // If we have both processed and total, calculate percentage
            if (totalDocs > 0 && docsProcessed >= 0) {
              progressPercentage = Math.round((docsProcessed / totalDocs) * 100);
              progressPercentage = Math.max(0, Math.min(100, progressPercentage));
            } else if ((item.status as string) === 'completed') {
              // For completed jobs, special logic for delta vs full crawls
              if (isDeltaCrawl) {
                // Delta crawl completed - show 100% regardless of documents found
                progressPercentage = 100;
                if (docsProcessed === 0) {
                  displayText = 'no new docs (up to date)';
                  totalDocs = 0; // Don't show misleading totals
                } else {
                  displayText = 'new docs found';
                  totalDocs = docsProcessed;
                }
              } else {
                // Full crawl completed
                progressPercentage = 100;
                totalDocs = docsProcessed > 0 ? docsProcessed : totalDocs;
              }
            } else if (docsProcessed > 0 && totalDocs === 0) {
              // If we have processed docs but no total, show processed count
              totalDocs = docsProcessed;
              progressPercentage = (item.status as string) === 'completed' ? 100 : 
                                 (item.status === 'cancelled' || item.status === 'stopped') ? Math.max(0, Math.min(95, Math.round((docsProcessed / Math.max(1, totalDocs)) * 100))) :
                                 item.status === 'running' ? 50 : 0; // Estimate for running jobs
            } else if ((item.status as string) === 'completed' && docsProcessed === 0 && totalDocs === 0) {
              // Completed job with no documents found - check reason from progress
              const currentItem = progress?.current_item || '';
              if (isDeltaCrawl) {
                progressPercentage = 100;
                displayText = 'no new docs (up to date)';
              } else if (currentItem.includes('matching filters')) {
                progressPercentage = 100;
                displayText = 'no docs match filters';
              } else if (currentItem.includes('No documents found in source')) {
                progressPercentage = 100;
                displayText = 'no docs in source';
              } else {
                progressPercentage = 100;
                displayText = 'no docs found';
              }
            } else if ((item.status as string) === 'running' && progress) {
              // For running jobs, ensure progress doesn't exceed 100% and handle resets
              if ((progress as any).percentage !== undefined) {
                // Ensure percentage is a number, not an object
                const percentage = (progress as any).percentage;
                progressPercentage = Math.max(0, Math.min(100, typeof percentage === 'number' ? percentage : 0));
              } else if (totalDocs > 0) {
                progressPercentage = Math.round((docsProcessed / totalDocs) * 100);
                progressPercentage = Math.max(0, Math.min(100, progressPercentage));
              }
              
              // Detect if this might be a job restart (running with very low progress)
              if (progressPercentage < 10 && docsProcessed < 5 && (item.runCount || 0) > 1) {
                displayText = 'restarted - processing';
              }
            }
          } else {
            // Fallback when no progress object exists
            docsProcessed = item.documentsProcessed || 0;
            totalDocs = item.documentsDiscovered || docsProcessed;
            
            if (item.status === 'completed') {
              progressPercentage = 100;
              if (isDeltaCrawl && docsProcessed === 0) {
                displayText = 'no new docs (up to date)';
                totalDocs = 0;
              } else if (docsProcessed === 0) {
                // Check if we have any indication this was due to filters
                const hasFileFilters = item.configuration?.file_types || item.config?.configuration?.file_types;
                const hasLibraryFilters = item.configuration?.libraries || item.config?.configuration?.libraries;
                const hasAgeFilters = item.configuration?.max_file_age_enabled || item.config?.configuration?.max_file_age_enabled;
                
                if (hasFileFilters || hasLibraryFilters || hasAgeFilters) {
                  displayText = 'no docs match filters';
                } else {
                  displayText = 'no docs found';
                }
                totalDocs = 0;
              }
            } else if (docsProcessed > 0 && totalDocs > 0) {
              progressPercentage = Math.round((docsProcessed / totalDocs) * 100);
              progressPercentage = Math.max(0, Math.min(100, progressPercentage));
            }
          }

          return (
            <Stack tokens={{ childrenGap: 4 }}>
              {item.status === 'running' ? (
                <>
                  <ProgressIndicator
                    percentComplete={(typeof progressPercentage === 'number' ? progressPercentage : 0) / 100}
                    description={`${typeof progressPercentage === 'number' ? progressPercentage : 0}%`}
                    styles={{
                      root: { width: '100px' },
                      progressBar: { 
                        backgroundColor: (typeof progressPercentage === 'number' && progressPercentage === 100) ? '#107c10' : '#0078d4' 
                      }
                    }}
                  />
                  <Text variant="small" style={{ color: '#666' }}>
                    {docsProcessed.toLocaleString()}{totalDocs > 0 ? ` / ${totalDocs.toLocaleString()}` : ''} docs
                  </Text>
                  {progress && progress.phase && (
                    <Text variant="small" style={{ color: '#666', fontStyle: 'italic' }}>
                      {progress.phase}: {progress.current_item || 'Processing...'}
                    </Text>
                  )}
                </>
              ) : (
                <>
                  <Text variant="small" style={{ 
                    fontWeight: 500,
                    color: item.status === 'completed' ? '#107c10' : 
                           (item.status === 'error' || item.status === 'failed') ? '#d83b01' :
                           (item.status === 'cancelled' || item.status === 'stopped') ? '#ff8c00' : '#666'
                  }}>
                    {typeof progressPercentage === 'number' ? progressPercentage : 0}% {item.status === 'completed' ? 'completed' : 
                                          (item.status === 'error' || item.status === 'failed') ? 'failed' :
                                          (item.status === 'cancelled' || item.status === 'stopped') ? 'stopped' : 'ready'}
                  </Text>
                  <Text variant="small" style={{ color: '#666' }}>
                    {displayText === 'docs processed' 
                      ? `${docsProcessed.toLocaleString()} docs processed`
                      : displayText === 'no new docs (up to date)'
                      ? '✅ No new docs (up to date)'
                      : displayText === 'new docs found'
                      ? `${docsProcessed.toLocaleString()} new docs found`
                      : displayText === 'no docs found'
                      ? 'ℹ️ No documents found'
                      : displayText === 'no docs match filters'
                      ? 'ℹ️ No documents match job filters'
                      : displayText === 'no docs in source'
                      ? 'ℹ️ No documents in source locations'
                      : `${docsProcessed.toLocaleString()} docs processed`
                    }
                  </Text>
                </>
              )}
            </Stack>
          );
        }
      },
      {
        key: 'actions',
        name: 'Actions',
        minWidth: 200,
        onRender: (item: Job) => (
          <Stack horizontal tokens={{ childrenGap: 8 }}>
            {/* Start/Stop/Restart/Rerun buttons based on status */}
            {item.status === 'created' || item.status === 'error' || item.status === 'failed' ? (
              <IconButton
                iconProps={{ iconName: 'Play' }}
                title="Start Job"
                onClick={() => handleRunJob(item.id)}
                styles={{
                  root: { width: '40px', height: '40px' },
                  icon: { fontSize: '18px' }
                }}
              />
            ) : null}
            
            {/* For stopped/cancelled/paused jobs, provide Start option along with Rerun/Full Crawl */}
            {(item.status === 'stopped' || item.status === 'cancelled' || item.status === 'paused') ? (
              <>
                <IconButton
                  iconProps={{ iconName: 'Play' }}
                  title="Start Job"
                  onClick={() => handleStartJob(item.id)}
                  styles={{
                    root: { width: '40px', height: '40px', backgroundColor: '#107c10' },
                    icon: { fontSize: '18px', color: 'white' }
                  }}
                />
                <IconButton
                  iconProps={{ iconName: 'Redo' }}
                  title="Rerun Job (Delta Crawl)"
                  onClick={() => handleRerunJob(item.id)}
                  styles={{
                    root: { width: '40px', height: '40px' },
                    icon: { fontSize: '18px' }
                  }}
                />
                <IconButton
                  iconProps={{ iconName: 'Refresh' }}
                  title="Force Full Crawl"
                  onClick={() => handleFullCrawlJob(item.id)}
                  styles={{
                    root: { width: '40px', height: '40px' },
                    icon: { fontSize: '18px' }
                  }}
                />
              </>
            ) : null}
            
            {/* For completed jobs, show Rerun and Full Crawl options */}
            {item.status === 'completed' ? (
              <>
                <IconButton
                  iconProps={{ iconName: 'Redo' }}
                  title="Rerun Job (Delta Crawl)"
                  onClick={() => handleRerunJob(item.id)}
                  styles={{
                    root: { width: '40px', height: '40px' },
                    icon: { fontSize: '18px' }
                  }}
                />
                <IconButton
                  iconProps={{ iconName: 'Refresh' }}
                  title="Force Full Crawl"
                  onClick={() => handleFullCrawlJob(item.id)}
                  styles={{
                    root: { width: '40px', height: '40px' },
                    icon: { fontSize: '18px' }
                  }}
                />
              </>
            ) : null}
            
            {(item.status === 'running' || item.status === 'active') ? (
              <>
                <IconButton
                  iconProps={{ iconName: 'Stop' }}
                  title="Stop Job"
                  onClick={() => handleStopJob(item.id)}
                  styles={{
                    root: { width: '40px', height: '40px' },
                    icon: { fontSize: '18px' }
                  }}
                />
                <IconButton
                  iconProps={{ iconName: 'Refresh' }}
                  title="Restart Job"
                  onClick={() => handleRestartJob(item.id)}
                  styles={{
                    root: { width: '40px', height: '40px' },
                    icon: { fontSize: '18px' }
                  }}
                />
                <IconButton
                  iconProps={{ iconName: 'Blocked' }}
                  title="Force Reset (if stuck)"
                  onClick={() => handleForceResetJob(item.id)}
                  styles={{
                    root: { width: '40px', height: '40px', backgroundColor: '#ff4b4b' },
                    icon: { fontSize: '18px', color: 'white' }
                  }}
                />
              </>
            ) : null}

            {/* For jobs that might be stuck (created that won't start, disabled, etc.) - show force reset */}
            {(item.status === 'created' || item.status === 'disabled' || item.status === 'scheduled' || 
              (item.status as string) === 'starting' || (item.status as string) === 'queued' || (item.status as string) === 'processing') ? (
              <IconButton
                iconProps={{ iconName: 'Blocked' }}
                title="Force Reset Stuck Job"
                onClick={() => handleForceResetJob(item.id)}
                styles={{
                  root: { width: '40px', height: '40px', backgroundColor: '#ff4b4b' },
                  icon: { fontSize: '18px', color: 'white' }
                }}
              />
            ) : null}

            {/* Always show Edit and Delete buttons */}
            <IconButton
              iconProps={{ iconName: 'Edit' }}
              title="Edit Job"
              onClick={() => handleEditJob(item)}
              styles={{
                root: { width: '40px', height: '40px' },
                icon: { fontSize: '18px' }
              }}
            />
            
            <IconButton
              iconProps={{ iconName: 'Delete' }}
              title="Delete Job"
              onClick={() => {
                setJobToDelete(item);
                setShowDeleteConfirm(true);
              }}
              styles={{
                root: { width: '40px', height: '40px' },
                icon: { fontSize: '18px' }
              }}
            />

            {/* More actions menu for additional options */}
            <IconButton
              iconProps={{ iconName: 'More' }}
              title="More Actions"
              styles={{
                root: { width: '40px', height: '40px' },
                icon: { fontSize: '18px' }
              }}
              menuProps={{
                items: [
                  {
                    key: 'view',
                    text: 'View Details',
                    iconProps: { iconName: 'View' },
                    onClick: () => {
                      setSelectedJob(item);
                      setShowJobDetailsPanel(true);
                    }
                  },
                  {
                    key: 'duplicate',
                    text: 'Duplicate Job',
                    iconProps: { iconName: 'Copy' },
                    onClick: () => handleDuplicateJob(item)
                  },
                  {
                    key: 'logs',
                    text: 'View Logs',
                    iconProps: { iconName: 'AlignLeft' },
                    onClick: () => {
                      setSelectedJob(item);
                      loadJobLogs(item.id);
                    }
                  },
                  {
                    key: 'clearLogs',
                    text: 'Clear Logs',
                    iconProps: { iconName: 'Delete' },
                    onClick: () => clearJobLogs(item.id)
                  }
                ]
              }}
            />
          </Stack>
        )
      }
    ];

    return (
      <DetailsList
        items={filteredJobs}
        columns={jobColumns}
        selectionMode={SelectionMode.none}
        className={styles.jobsList}
      />
    );
  };

  const handleRunJob = async (jobId: string) => {
    try {
      const response = await authenticatedFetch(`/api/connector-hub/jobs/${jobId}/start`, {
        method: 'POST'
      });
      const result = await response.json();
      if (result.success) {
        // Job started successfully - no popup needed, just reload
        loadJobManagementData(); // Reload to update status
      } else {
        alert(`Failed to start job: ${result.error}`);
      }
    } catch (error) {
      console.error('Error running job:', error);
      alert(`Error starting job: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleStopJob = async (jobId: string) => {
    try {
      const response = await authenticatedFetch(`/api/connector-hub/jobs/${jobId}/stop`, {
        method: 'POST'
      });
      const result = await response.json();
      if (result.success) {
        alert(`Job stopped successfully!`);
        // Wait a moment then reload to update status
        setTimeout(() => {
          loadJobManagementData();
        }, 1000);
      } else {
        alert(`Failed to stop job: ${result.error}`);
      }
    } catch (error) {
      console.error('Error stopping job:', error);
      alert(`Error stopping job: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleRestartJob = async (jobId: string) => {
    try {
      // For running jobs, stop and restart
      const stopResponse = await authenticatedFetch(`/api/connector-hub/jobs/${jobId}/stop`, {
        method: 'POST'
      });
      
      // Wait for stop to complete, then start again
      setTimeout(async () => {
        try {
          const startResponse = await authenticatedFetch(`/api/connector-hub/jobs/${jobId}/start`, {
            method: 'POST'
          });
          const result = await startResponse.json();
          if (result.success) {
            // Job restarted successfully - wait for status update then reload
            setTimeout(() => {
              loadJobManagementData();
            }, 1000);
          } else {
            alert(`Failed to restart job: ${result.error}`);
          }
        } catch (error) {
          console.error('Error starting job after stop:', error);
          alert(`Error restarting job: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      }, 2000); // Wait 2 seconds for stop to complete
    } catch (error) {
      console.error('Error restarting job:', error);
      alert(`Error restarting job: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // New handler for starting stopped/completed jobs
  const handleStartJob = async (jobId: string) => {
    try {
      const startResponse = await authenticatedFetch(`/api/connector-hub/jobs/${jobId}/start`, {
        method: 'POST'
      });
      const result = await startResponse.json();
      
      if (result.success) {
        // Job started successfully - wait for status update then reload
        setTimeout(() => {
          loadJobManagementData();
        }, 1000);
      } else {
        alert(`Failed to start job: ${result.error}`);
      }
    } catch (error) {
      console.error('Error starting job:', error);
      alert(`Error starting job: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleRerunJob = async (jobId: string) => {
    try {
      // Use the new delta-crawl endpoint
      const rerunResponse = await authenticatedFetch(`/api/connector-hub/jobs/${jobId}/delta-crawl`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });
      const result = await rerunResponse.json();
      
      if (result.success) {
        // Job rerun started successfully - wait a moment then reload to show progress
        setTimeout(() => {
          loadJobManagementData();
        }, 1000); // Give the backend time to update status
      } else {
        alert(`Failed to rerun job: ${result.error}`);
      }
    } catch (error) {
      console.error('Error rerunning job:', error);
      alert(`Error rerunning job: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleFullCrawlJob = async (jobId: string) => {
    try {
      // Force a full crawl instead of delta
      const fullCrawlResponse = await authenticatedFetch(`/api/connector-hub/jobs/${jobId}/full-crawl`, {
        method: 'POST'
      });
      const result = await fullCrawlResponse.json();
      
      if (result.success) {
        // Full crawl started successfully - wait a moment then reload to show progress
        setTimeout(() => {
          loadJobManagementData();
        }, 1000); // Give the backend time to update status
      } else {
        alert(`Failed to start full crawl: ${result.error}`);
      }
    } catch (error) {
      console.error('Error starting full crawl:', error);
      alert(`Error starting full crawl: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleForceResetJob = async (jobId: string) => {
    try {
      const resetResponse = await authenticatedFetch(`/api/connector-hub/jobs/${jobId}/force-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });
      const result = await resetResponse.json();
      
      if (result.success) {
        alert('Job status has been reset successfully. You can now rerun the job.');
        // Reload to refresh the jobs list
        setTimeout(() => {
          loadJobManagementData();
        }, 1000); // Give the backend time to update status
      } else {
        alert(`Failed to reset job: ${result.error}`);
      }
    } catch (error) {
      console.error('Error resetting job:', error);
      alert(`Error resetting job: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleDeleteJob = async (jobId: string) => {
    try {
      const response = await authenticatedFetch(`/api/connector-hub/jobs/${jobId}`, {
        method: 'DELETE'
      });
      
      const result = await response.json();
      
      if (result.success) {
        alert(`Job "${jobToDelete?.name || jobId}" has been deleted successfully.`);
        setShowDeleteConfirm(false);
        setJobToDelete(null);
        loadJobManagementData(); // Reload to refresh the jobs list
      } else {
        alert(`Failed to delete job: ${result.error}`);
      }
    } catch (error) {
      console.error('Error deleting job:', error);
      alert(`Error deleting job: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleBulkDeleteJobs = async () => {
    if (selectedJobs.size === 0) return;

    try {
      setBulkDeleteLoading(true);
      
      const jobIds = Array.from(selectedJobs);
      
      const response = await authenticatedFetch('/api/connector-hub/jobs/bulk-delete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ job_ids: jobIds })
      });

      const result = await response.json();
      
      if (result.success) {
        const { successful_deletions, failed_deletions } = result.summary;
        
        if (successful_deletions > 0) {
          alert(`Successfully initiated deletion of ${successful_deletions} job${successful_deletions > 1 ? 's' : ''}${failed_deletions > 0 ? `. ${failed_deletions} job${failed_deletions > 1 ? 's' : ''} failed to delete.` : '.'}`);
        } else {
          alert(`Failed to delete ${failed_deletions} job${failed_deletions > 1 ? 's' : ''}.`);
        }
      } else {
        alert(`Failed to delete jobs: ${result.error}`);
      }

      // Clear selection and reload jobs
      clearSelection();
      setShowBulkDeleteConfirm(false);
      loadJobManagementData();
      
    } catch (error) {
      console.error('Error during bulk delete:', error);
      alert(`Error deleting jobs: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  const handleEditJob = async (job: Job) => {
    // Load available indices first
    await loadAvailableIndices();
    
    setEditingJob(job);
    
    // Properly extract index name from various possible locations
    const currentIndexName = job.targetIndex || job.index_name || job.configuration?.index_name || '';
    
    setEditFormData({
      name: job.name || '',
      description: job.description || '',
      targetIndex: currentIndexName,
      index_name: currentIndexName, // Ensure index_name is set for the form
      configuration: { 
        ...job.configuration,
        index_name: currentIndexName // Ensure it's also in configuration
      },
      // Extract common configuration fields for easy editing
      site_url: job.configuration?.site_url || (Array.isArray(job.configuration?.sites) ? job.configuration?.sites[0] : ''),
      site_urls: job.configuration?.site_urls || (job.configuration?.site_url ? [job.configuration.site_url] : []),
      sites: job.configuration?.sites || [],
      file_types: job.configuration?.file_types || ['.pdf', '.docx', '.pptx', '.xlsx', '.txt', '.html', '.msg', '.aspx'],
      libraries: job.configuration?.libraries || [],
      max_documents: job.configuration?.max_documents || undefined,
      crawl_subsites: job.configuration?.crawl_subsites !== false,
      enable_acl: job.configuration?.enable_acl !== false,
      include_acl: job.configuration?.include_acl !== false,
      enable_embeddings: job.configuration?.enable_embeddings !== false,


      enable_vectoring: job.configuration?.enable_vectoring !== false,
      enable_multilingual_entities: job.configuration?.enable_multilingual_entities === true,
      embedding_model: job.configuration?.embedding_model || 'text-embedding-3-large',
      chunk_size: job.configuration?.chunk_size || 2000,
      chunk_overlap: job.configuration?.chunk_overlap || 200,
      max_file_age_enabled: job.configuration?.max_file_age_enabled || false,
      max_file_age_days: job.configuration?.max_file_age_days || 90,
      batch_size: job.configuration?.batch_size || 10,
      max_file_size_mb: job.configuration?.max_file_size_mb || 50,
      upload_only: job.configuration?.upload_only || false,
      content_types: job.configuration?.content_types || ['documents', 'folders', 'lists', 'pages'],
      // SharePoint On-Premises authentication
      username: job.configuration?.username || '',
      password: job.configuration?.password || '',
      domain: job.configuration?.domain || '',
      // Public Web Connector specific fields
      start_urls: job.configuration?.start_urls || [],
      allowed_domains: job.configuration?.allowed_domains || [],
      max_depth: job.configuration?.max_depth || 2,
      max_pages: job.configuration?.max_pages || 100,
      crawl_delay: job.configuration?.crawl_delay || 1,
      respect_robots_txt: job.configuration?.respect_robots_txt !== undefined ? job.configuration.respect_robots_txt : true,
      follow_redirects: job.configuration?.follow_redirects !== undefined ? job.configuration.follow_redirects : true,
      enable_javascript: job.configuration?.enable_javascript || false,
      include_paths: job.configuration?.include_paths || [],
      exclude_paths: job.configuration?.exclude_paths || [],
      user_agent: job.configuration?.user_agent || 'BynetAI-WebCrawler/1.0',
      wait_time: job.configuration?.wait_time || 3
    });
    setShowEditJobPanel(true);
  };

  const handleSaveJobEdit = async () => {
    if (!editingJob) return;

    // Validate index name if creating new index or using existing
    const indexName = editFormData.index_name || editFormData.configuration?.index_name || '';
    const validationError = validateIndexName(indexName);
    
    if (validationError) {
      setIndexValidationError(validationError);
      alert(`Invalid index name: ${validationError}`);
      return;
    }

    // Check if index already exists when creating new index
    if ((editFormData.create_new_index || editFormData.configuration?.create_new_index) && 
        availableIndices.includes(indexName)) {
      setIndexValidationError('Index name already exists. Please choose a different name.');
      alert('Index name already exists. Please choose a different name.');
      return;
    }

    setIndexValidationError(''); // Clear any previous errors

    try {
      setEditLoading(true);
      
      // Build updated configuration
      const updatedConfiguration = {
        ...editFormData.configuration,
        // SharePoint Online fields
        site_url: editFormData.site_url,
        site_urls: editFormData.site_urls,
        file_types: editFormData.file_types,
        libraries: editFormData.libraries,
        max_documents: editFormData.max_documents,
        crawl_subsites: editFormData.crawl_subsites,
        enable_acl: editFormData.enable_acl,
        max_depth: editFormData.max_depth,
        enable_ocr: editFormData.enable_ocr,
        enable_embeddings: editFormData.enable_embeddings,
        embedding_model: editFormData.embedding_model,
        chunk_size: editFormData.chunk_size,
        chunk_overlap: editFormData.chunk_overlap,
        max_file_age_enabled: editFormData.max_file_age_enabled,
        max_file_age_days: editFormData.max_file_age_days,
        // SharePoint On-Premises fields
        sites: editFormData.sites,
        username: editFormData.username,
        password: editFormData.password,
        domain: editFormData.domain,
        content_types: editFormData.content_types,
        include_acl: editFormData.include_acl,
        enable_vectoring: editFormData.enable_vectoring,
        max_file_size_mb: editFormData.max_file_size_mb,
        batch_size: editFormData.batch_size,
        upload_only: editFormData.upload_only,
        // Public Web Connector fields
        start_urls: editFormData.start_urls,
        allowed_domains: editFormData.allowed_domains,
        max_pages: editFormData.max_pages,
        crawl_delay: editFormData.crawl_delay,
        respect_robots_txt: editFormData.respect_robots_txt,
        follow_redirects: editFormData.follow_redirects,
        enable_javascript: editFormData.enable_javascript,
        include_paths: editFormData.include_paths,
        exclude_paths: editFormData.exclude_paths,
        user_agent: editFormData.user_agent,
        wait_time: editFormData.wait_time
      };

      const updateData = {
        name: editFormData.name,
        description: editFormData.description,
        targetIndex: editFormData.targetIndex,
        configuration: updatedConfiguration
      };

      const response = await authenticatedFetch(`/api/connector-hub/jobs/${editingJob.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      const result = await response.json();
      
      if (result.success) {
        alert('Job updated successfully!');
        setShowEditJobPanel(false);
        setEditingJob(null);
        setEditFormData({});
        await loadJobManagementData(); // Reload jobs list
      } else {
        alert(`Failed to update job: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating job:', error);
      alert(`Error updating job: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setEditLoading(false);
    }
  };

  const handleDuplicateJob = (job: Job) => {
    alert('To duplicate a job, please create a new job from the Connectors tab with similar settings.');
  };

  const loadJobLogs = async (jobId: string) => {
    setLogsLoading(true);
    setJobLogs('');
    try {
      // Request more logs for better visibility (1000 lines)
      const response = await authenticatedFetch(`/api/connector-hub/jobs/${jobId}/logs?lines=1000`);
      const result = await response.json();
      
      if (result.success) {
        // Handle both new structured format and old string format
        if (Array.isArray(result.logs) && result.logs.length > 0) {
          // New structured format
          setJobLogs(result.logs);
        } else if (typeof result.logs === 'string' && result.logs.trim()) {
          // Old string format - keep for backward compatibility
          setJobLogs(result.logs);
        } else {
          setJobLogs('No logs available for this job yet. Logs will appear here once the job starts running.');
        }
      } else {
        setJobLogs(`❌ Error loading logs: ${result.error || 'Unknown error'}\n\nThis might happen if:\n• The job hasn't started yet\n• The job logs are still being written\n• There's a connectivity issue\n\nTry refreshing in a few moments.`);
      }
    } catch (error) {
      console.error('Error loading job logs:', error);
      setJobLogs(`❌ Failed to load logs: ${error instanceof Error ? error.message : 'Unknown error'}\n\nPlease check your connection and try again.`);
    } finally {
      setLogsLoading(false);
      setShowLogsPanel(true);
    }
  };

  const clearJobLogs = async (jobId: string) => {
    const confirmed = window.confirm(
      'Are you sure you want to clear all logs for this job? This action cannot be undone.'
    );
    
    if (!confirmed) return;

    try {
      const response = await authenticatedFetch(`/api/connector-hub/jobs/${jobId}/logs`, {
        method: 'DELETE'
      });
      
      const result = await response.json();
      
      if (result.success) {
        alert(`Logs cleared successfully for job!`);
        // If the logs panel is open, refresh it to show empty logs
        if (showLogsPanel && selectedJob?.id === jobId) {
          setJobLogs('Logs have been cleared for this job.');
        }
      } else {
        alert(`Failed to clear logs: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error clearing job logs:', error);
      alert(`Error clearing logs: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const commandBarItems: ICommandBarItemProps[] = [
    {
      key: 'create',
      text: 'Create New Job',
      iconProps: { iconName: 'Add', styles: { root: { fontSize: '18px' } } },
      onClick: () => {
        if (onNavigateToConnectors) {
          onNavigateToConnectors();
        }
      }
    },
    {
      key: 'selectAll',
      text: selectedJobs.size === filteredJobs.length && filteredJobs.length > 0 ? 'Deselect All' : 'Select All',
      iconProps: { iconName: 'CheckboxComposite', styles: { root: { fontSize: '18px' } } },
      disabled: filteredJobs.length === 0,
      onClick: () => {
        if (selectedJobs.size === filteredJobs.length && filteredJobs.length > 0) {
          clearSelection();
        } else {
          selectAllJobs();
        }
      }
    },
    {
      key: 'bulkDelete',
      text: `Delete Selected (${selectedJobs.size})`,
      iconProps: { iconName: 'Delete', styles: { root: { fontSize: '18px', color: '#d13438' } } },
      disabled: selectedJobs.size === 0,
      onClick: () => setShowBulkDeleteConfirm(true),
      buttonStyles: {
        root: {
          backgroundColor: selectedJobs.size > 0 ? '#fdf2f2' : undefined,
          border: selectedJobs.size > 0 ? '1px solid #fecaca' : undefined
        },
        rootHovered: {
          backgroundColor: selectedJobs.size > 0 ? '#fee2e2' : undefined
        }
      }
    },
    {
      key: 'refresh',
      text: 'Refresh Progress',
      iconProps: { iconName: 'Refresh', styles: { root: { fontSize: '18px' } } },
      onClick: () => { 
        console.log('Manual refresh triggered');
        loadJobManagementData(); 
      }
    }
  ];

  const statusFilterOptions: IDropdownOption[] = [
    { key: 'all', text: 'All Status' },
    { key: 'created', text: 'Created' },
    { key: 'active', text: 'Active' },
    { key: 'running', text: 'Running' },
    { key: 'paused', text: 'Paused' },
    { key: 'disabled', text: 'Disabled' },
    { key: 'error', text: 'Error' },
    { key: 'scheduled', text: 'Scheduled' },
    { key: 'completed', text: 'Completed' }
  ];

  const connectorFilterOptions: IDropdownOption[] = [
    { key: 'all', text: 'All Connectors' },
    ...Array.from(new Set(jobs.map(job => job.connectorType).filter(Boolean))).map(type => ({
      key: type!,
      text: String(type || '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    }))
  ];

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size={SpinnerSize.large} label="Loading job management..." />
      </div>
    );
  }

  return (
    <div className={styles.jobManagement}>
      <Stack tokens={{ childrenGap: 24 }}>
        {/* Header */}
        <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
          <Stack tokens={{ childrenGap: 8 }}>
            <Text variant="xxLarge" style={{ fontWeight: 700, color: '#1a365d' }}>
              Job Management
            </Text>
            <Text variant="medium" style={{ color: '#64748b' }}>
              Monitor running jobs, view history, and manage scheduled tasks • Create new jobs from the Connectors tab
            </Text>
            <MessageBar messageBarType={MessageBarType.info} isMultiline={false}>
              💡 Progress updates manually - click "Refresh Progress" to see latest job status • Select multiple jobs to delete them in bulk
            </MessageBar>
          </Stack>
          <CommandBar items={commandBarItems} />
        </Stack>

        {/* Selection Summary */}
        {selectedJobs.size > 0 && (
          <MessageBar 
            messageBarType={MessageBarType.info}
            actions={
              <div>
                <DefaultButton
                  text="Clear Selection"
                  onClick={() => {
                    clearSelection();
                  }}
                  styles={{ root: { marginLeft: 8 } }}
                />
              </div>
            }
          >
            {selectedJobs.size} job{selectedJobs.size > 1 ? 's' : ''} selected
          </MessageBar>
        )}

        {/* Filters */}
        <Stack horizontal tokens={{ childrenGap: 16 }} verticalAlign="end">
          <SearchBox
            placeholder="Search jobs..."
            value={searchQuery}
            onChange={(_, newValue) => setSearchQuery(newValue || '')}
            styles={{ root: { width: 300 } }}
          />
          <Dropdown
            placeholder="Filter by status"
            options={statusFilterOptions}
            selectedKey={statusFilter}
            onChange={(_, option) => setStatusFilter(option?.key as string)}
            styles={{ root: { width: 150 } }}
          />
          <Dropdown
            placeholder="Filter by connector"
            options={connectorFilterOptions}
            selectedKey={connectorFilter}
            onChange={(_, option) => setConnectorFilter(option?.key as string)}
            styles={{ root: { width: 180 } }}
          />
        </Stack>

        {/* Jobs List */}
        {filteredJobs.length > 0 ? (
          renderJobsList()
        ) : (
          <div className={styles.emptyState}>
            <ConnectorRegular style={{ fontSize: 48, color: '#d1d5db' }} />
            <Text variant="large" style={{ fontWeight: 600, color: '#374151' }}>
              No Jobs Found
            </Text>
            <Text style={{ color: '#6b7280' }}>
              {jobs.length === 0 
                ? 'Create your first data connector job to get started'
                : 'No jobs match your current filters'
              }
            </Text>
            {jobs.length === 0 && (
              <PrimaryButton
                text="Create First Job"
                onClick={() => {
                  if (onNavigateToConnectors) {
                    onNavigateToConnectors();
                  }
                }}
                styles={{
                  root: { marginTop: '16px', height: '42px' },
                  label: { fontSize: '15px' }
                }}
              />
            )}
          </div>
        )}

        {/* Job Details Panel */}
        <Panel
          isOpen={showJobDetailsPanel}
          onDismiss={() => setShowJobDetailsPanel(false)}
          headerText={`Job Details - ${selectedJob ? getJobDisplayName(selectedJob) : 'Unknown Job'}`}
          type={PanelType.medium}
          className={styles.jobDetailsPanel}
        >
          {selectedJob && (() => {
            const theme = getTheme();
            
            return (
            <Stack tokens={{ childrenGap: 16 }}>
              {/* Job Overview */}
              <Stack tokens={{ childrenGap: 12 }}>
                <Text variant="mediumPlus" style={{ fontWeight: 600, color: '#1a365d' }}>
                  📋 Job Overview
                </Text>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <Stack tokens={{ childrenGap: 4 }}>
                    <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>Job Name</Text>
                    <Text variant="small">{getJobDisplayName(selectedJob)}</Text>
                  </Stack>
                  
                  <Stack tokens={{ childrenGap: 4 }}>
                    <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>Job ID</Text>
                    <Text variant="small" style={{ fontFamily: 'monospace', fontSize: '11px' }}>{selectedJob.id}</Text>
                  </Stack>
                  
                  <Stack tokens={{ childrenGap: 4 }}>
                    <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>Connector Type</Text>
                    <Text variant="small">{getJobConnectorType(selectedJob)}</Text>
                  </Stack>
                  
                  <Stack tokens={{ childrenGap: 4 }}>
                    <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>Target Index</Text>
                    <Text variant="small">{selectedJob.targetIndex || selectedJob.index_name || 'N/A'}</Text>
                  </Stack>
                  
                  <Stack tokens={{ childrenGap: 4 }}>
                    <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>Status</Text>
                    <div className={`${styles.statusBadge} ${styles[selectedJob.status as string]}`}>
                      {selectedJob.status}
                    </div>
                  </Stack>
                  
                  <Stack tokens={{ childrenGap: 4 }}>
                    <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>Crawl Mode</Text>
                    <Text variant="small" style={{ 
                      color: selectedJob.crawl_mode === 'delta' ? '#0078d4' : '#ca5010',
                      fontWeight: 500 
                    }}>
                      {selectedJob.crawl_mode === 'delta' ? '🔄 Delta Crawl' : '🔄 Full Crawl'}
                    </Text>
                  </Stack>
                  
                  <Stack tokens={{ childrenGap: 4 }}>
                    <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>Created</Text>
                    <Text variant="small">
                      {selectedJob.created || selectedJob.created_at 
                        ? new Date(selectedJob.created || selectedJob.created_at!).toLocaleString()
                        : 'N/A'
                      }
                    </Text>
                  </Stack>
                  
                  {selectedJob.lastFullCrawl && (
                    <Stack tokens={{ childrenGap: 4 }}>
                      <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>Last Full Crawl</Text>
                      <Text variant="small">
                        {new Date(selectedJob.lastFullCrawl).toLocaleString()}
                      </Text>
                    </Stack>
                  )}
                  
                  {selectedJob.last_crawl_timestamp && (
                    <Stack tokens={{ childrenGap: 4 }}>
                      <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>Last Crawl</Text>
                      <Text variant="small">
                        {new Date(selectedJob.last_crawl_timestamp).toLocaleString()}
                      </Text>
                    </Stack>
                  )}
                </div>
                
                {selectedJob.description && (
                  <Stack tokens={{ childrenGap: 4 }}>
                    <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>Description</Text>
                    <Text variant="small">{selectedJob.description}</Text>
                  </Stack>
                )}
              </Stack>

              <Separator />

              {/* Job Configuration */}
              <Stack tokens={{ childrenGap: 12 }}>
                <Text variant="mediumPlus" style={{ fontWeight: 600, color: '#1a365d' }}>
                  ⚙️ Configuration
                </Text>
                
                {selectedJob.configuration || selectedJob.config ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    {(() => {
                      const config = selectedJob.configuration || selectedJob.config?.configuration || selectedJob.config || {};
                      
                      return Object.entries(config).map(([key, value]) => {
                        let displayValue = value;
                        let displayKey = key;
                        
                        // Format specific configuration keys
                        if (key === 'site_url') {
                          displayKey = 'SharePoint Site URL';
                        }
                        else if (key === 'site_urls' && Array.isArray(value)) {
                          displayKey = 'SharePoint Site URLs';
                          displayValue = value.length > 0 ? (
                            value.length === 1 ? value[0] : `${value.length} sites: ${value.slice(0, 2).join(', ')}${value.length > 2 ? '...' : ''}`
                          ) : 'None configured';
                        }
                        else if (key === 'sourceUrls' && Array.isArray(value)) {
                          displayKey = 'Source URLs';
                          displayValue = value.length > 0 ? (
                            value.length === 1 ? value[0] : `${value.length} URLs: ${value.slice(0, 2).join(', ')}${value.length > 2 ? '...' : ''}`
                          ) : 'None configured';
                        }
                        else if (key === 'enable_embeddings') {
                          displayKey = 'AI Embeddings';
                          displayValue = value ? '✅ Enabled' : '❌ Disabled';
                        }
                        else if (key === 'enable_acl') {
                          displayKey = 'Access Control (ACL)';
                          displayValue = value ? '✅ Enabled' : '❌ Disabled';
                        }
                        else if (key === 'crawl_subsites') {
                          displayKey = 'Crawl Subsites';
                          displayValue = value ? '✅ Yes' : '❌ No';
                        }
                        else if (key === 'max_file_age_enabled') {
                          displayKey = 'File Age Filter';
                          displayValue = value ? '✅ Enabled' : '❌ Disabled';
                        }
                        else if (key === 'max_file_age_days') {
                          displayKey = 'Max File Age';
                          displayValue = value ? `${value} days` : 'N/A';
                        }
                        else if (key === 'file_types' && Array.isArray(value)) {
                          displayKey = 'File Types';
                          displayValue = value.join(', ');
                        }
                        else if (key === 'libraries' && Array.isArray(value)) {
                          displayKey = 'Libraries';
                          displayValue = value.length > 0 ? value.join(', ') : 'All libraries';
                        }
                        else if (key === 'content_types' && Array.isArray(value)) {
                          displayKey = 'Content Types';
                          displayValue = value.join(', ');
                        }
                        else {
                          // Format camelCase keys
                          displayKey = String(key).replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                        }
                        
                        return (
                          <Stack key={key} tokens={{ childrenGap: 4 }}>
                            <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>{displayKey}</Text>
                            
                            {/* Special handling for multiple URLs */}
                            {(key === 'site_urls' || key === 'sourceUrls') && Array.isArray(value) && value.length > 1 ? (
                              <Stack tokens={{ childrenGap: 2 }}>
                                <Text variant="small" style={{ color: '#0078d4', fontWeight: 500 }}>
                                  {value.length} sites configured:
                                </Text>
                                {value.map((url: string, index: number) => (
                                  <Text key={index} variant="small" style={{ 
                                    paddingLeft: '12px',
                                    wordBreak: 'break-all',
                                    fontSize: '11px',
                                    fontFamily: 'monospace',
                                    color: '#333'
                                  }}>
                                    • {url}
                                  </Text>
                                ))}
                              </Stack>
                            ) : (
                              <Text variant="small" style={{ 
                                wordBreak: 'break-all',
                                fontFamily: typeof displayValue === 'string' && displayValue.length > 50 ? 'monospace' : 'inherit',
                                fontSize: typeof displayValue === 'string' && displayValue.length > 50 ? '11px' : 'inherit'
                              }}>
                                {typeof displayValue === 'object' ? JSON.stringify(displayValue) : String(displayValue)}
                              </Text>
                            )}
                          </Stack>
                        );
                      });
                    })()}
                  </div>
                ) : (
                  <Text variant="medium" style={{ color: '#666', fontStyle: 'italic' }}>
                    No configuration details available
                  </Text>
                )}
              </Stack>

              <Separator />

              {/* Job Statistics */}
              <Stack tokens={{ childrenGap: 12 }}>
                <Text variant="mediumPlus" style={{ fontWeight: 600, color: '#1a365d' }}>
                  📊 Statistics & Performance
                </Text>
                
                {/* Office 365 Detailed Statistics */}
                {selectedJob.connectorType === 'office365' && selectedJob.completion_summary && (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px' }}>
                      <Stack tokens={{ childrenGap: 4 }}>
                        <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>📧 Emails Processed</Text>
                        <Text variant="mediumPlus" style={{ fontWeight: 600, color: '#107c10' }}>
                          {((selectedJob.completion_summary as any)?.emails_processed || 0).toLocaleString()}
                        </Text>
                      </Stack>
                      
                      <Stack tokens={{ childrenGap: 4 }}>
                        <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>✅ Emails Indexed</Text>
                        <Text variant="mediumPlus" style={{ fontWeight: 600, color: '#0078d4' }}>
                          {((selectedJob.completion_summary as any)?.emails_indexed || 0).toLocaleString()}
                        </Text>
                      </Stack>
                      
                      <Stack tokens={{ childrenGap: 4 }}>
                        <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>📁 Drive Items Processed</Text>
                        <Text variant="mediumPlus" style={{ fontWeight: 600, color: '#8B5CF6' }}>
                          {((selectedJob.completion_summary as any)?.drive_items_processed || 0).toLocaleString()}
                        </Text>
                      </Stack>
                      
                      <Stack tokens={{ childrenGap: 4 }}>
                        <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>💾 Drive Items Indexed</Text>
                        <Text variant="mediumPlus" style={{ fontWeight: 600, color: '#059669' }}>
                          {((selectedJob.completion_summary as any)?.drive_items_indexed || 0).toLocaleString()}
                        </Text>
                      </Stack>
                    </div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px' }}>
                      <Stack tokens={{ childrenGap: 4 }}>
                        <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>📎 Attachments</Text>
                        <Text variant="mediumPlus" style={{ fontWeight: 600, color: '#DC2626' }}>
                          {((selectedJob.completion_summary as any)?.attachments_processed || 0).toLocaleString()}
                        </Text>
                      </Stack>
                      
                      <Stack tokens={{ childrenGap: 4 }}>
                        <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>🔒 ACL Entries</Text>
                        <Text variant="mediumPlus" style={{ fontWeight: 600, color: '#F59E0B' }}>
                          {((selectedJob.completion_summary as any)?.acl_entries || 0).toLocaleString()}
                        </Text>
                      </Stack>
                      
                      <Stack tokens={{ childrenGap: 4 }}>
                        <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>🚫 Emails Filtered</Text>
                        <Text variant="mediumPlus" style={{ fontWeight: 600, color: '#6B7280' }}>
                          {((selectedJob.completion_summary as any)?.emails_filtered || 0).toLocaleString()}
                        </Text>
                      </Stack>
                      
                      <Stack tokens={{ childrenGap: 4 }}>
                        <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>📊 Total Indexed</Text>
                        <Text variant="mediumPlus" style={{ fontWeight: 600, color: '#16A34A' }}>
                          {((selectedJob.completion_summary as any)?.total_documents_indexed || 
                            selectedJob.completion_summary?.documents_indexed || 0).toLocaleString()}
                        </Text>
                      </Stack>
                    </div>
                  </>
                )}
                
                {/* SharePoint On-Premises Detailed Statistics */}
                {(selectedJob.connectorType === 'sharepoint_onprem' || selectedJob.connectorType === 'sharepoint-onprem') && selectedJob.completion_summary && (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px' }}>
                      <Stack tokens={{ childrenGap: 4 }}>
                        <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>🌐 Sites Crawled</Text>
                        <Text variant="mediumPlus" style={{ fontWeight: 600, color: '#107c10' }}>
                          {((selectedJob.completion_summary as any)?.sites_crawled || 
                            (selectedJob.completion_summary as any)?.sites_processed || 0).toLocaleString()}
                        </Text>
                      </Stack>
                      
                      <Stack tokens={{ childrenGap: 4 }}>
                        <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>📄 Documents Found</Text>
                        <Text variant="mediumPlus" style={{ fontWeight: 600, color: '#0078d4' }}>
                          {((selectedJob.completion_summary as any)?.documents_discovered || 
                            (selectedJob.completion_summary as any)?.documents_found || 0).toLocaleString()}
                        </Text>
                      </Stack>
                      
                      <Stack tokens={{ childrenGap: 4 }}>
                        <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>✅ Documents Processed</Text>
                        <Text variant="mediumPlus" style={{ fontWeight: 600, color: '#8B5CF6' }}>
                          {((selectedJob.completion_summary as any)?.documents_processed || 0).toLocaleString()}
                        </Text>
                      </Stack>
                      
                      <Stack tokens={{ childrenGap: 4 }}>
                        <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>📊 Documents Indexed</Text>
                        <Text variant="mediumPlus" style={{ fontWeight: 600, color: '#059669' }}>
                          {((selectedJob.completion_summary as any)?.documents_indexed || 0).toLocaleString()}
                        </Text>
                      </Stack>
                    </div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px' }}>
                      <Stack tokens={{ childrenGap: 4 }}>
                        <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>📁 Libraries Crawled</Text>
                        <Text variant="mediumPlus" style={{ fontWeight: 600, color: '#DC2626' }}>
                          {((selectedJob.completion_summary as any)?.libraries_crawled || 0).toLocaleString()}
                        </Text>
                      </Stack>
                      
                      <Stack tokens={{ childrenGap: 4 }}>
                        <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>📋 Lists Processed</Text>
                        <Text variant="mediumPlus" style={{ fontWeight: 600, color: '#F59E0B' }}>
                          {((selectedJob.completion_summary as any)?.lists_processed || 0).toLocaleString()}
                        </Text>
                      </Stack>
                      
                      <Stack tokens={{ childrenGap: 4 }}>
                        <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>🧩 Chunks Created</Text>
                        <Text variant="mediumPlus" style={{ fontWeight: 600, color: '#6B7280' }}>
                          {((selectedJob.completion_summary as any)?.chunks_created || 0).toLocaleString()}
                        </Text>
                      </Stack>
                      
                      <Stack tokens={{ childrenGap: 4 }}>
                        <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>⚡ Execution Time</Text>
                        <Text variant="mediumPlus" style={{ fontWeight: 600, color: '#16A34A' }}>
                          {((selectedJob.completion_summary as any)?.execution_time ? 
                            `${Math.round((selectedJob.completion_summary as any).execution_time)}s` : 'N/A')}
                        </Text>
                      </Stack>
                    </div>

                    {/* File Type Breakdown for SharePoint */}
                    {(selectedJob.completion_summary as any)?.file_type_stats && Object.keys((selectedJob.completion_summary as any)?.file_type_stats || {}).length > 0 && (
                      <Stack tokens={{ childrenGap: 8 }}>
                        <Text variant="medium" style={{ fontWeight: 600, color: theme.palette.themePrimary }}>
                          📁 File Type Breakdown
                        </Text>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '8px' }}>
                          {Object.entries((selectedJob.completion_summary as any)?.file_type_stats || {}).map(([fileType, count]) => (
                            <Stack key={fileType} tokens={{ childrenGap: 2 }} style={{ 
                              padding: '8px', 
                              backgroundColor: theme.palette.neutralLighterAlt, 
                              borderRadius: '4px',
                              border: `1px solid ${theme.palette.neutralLight}`
                            }}>
                              <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>
                                {fileType.toUpperCase()}
                              </Text>
                              <Text variant="medium" style={{ fontWeight: 600, color: theme.palette.themePrimary }}>
                                {(count as number).toLocaleString()}
                              </Text>
                            </Stack>
                          ))}
                        </div>
                      </Stack>
                    )}

                    {/* Content Types Breakdown */}
                    {(selectedJob.completion_summary as any)?.content_types_found && (selectedJob.completion_summary as any)?.content_types_found.length > 0 && (
                      <Stack tokens={{ childrenGap: 8 }}>
                        <Text variant="medium" style={{ fontWeight: 600, color: theme.palette.themePrimary }}>
                          📂 Content Types Found
                        </Text>
                        <div style={{ 
                          padding: '8px', 
                          backgroundColor: theme.palette.neutralLighterAlt, 
                          borderRadius: '4px',
                          border: `1px solid ${theme.palette.neutralLight}`
                        }}>
                          {(selectedJob.completion_summary as any)?.content_types_found.map((contentType: string, index: number) => (
                            <Text key={index} variant="small" style={{ 
                              display: 'inline-block', 
                              padding: '4px 8px',
                              margin: '2px',
                              backgroundColor: theme.palette.themeLighter,
                              borderRadius: '12px',
                              color: theme.palette.themePrimary,
                              fontSize: '11px'
                            }}>
                              {contentType}
                            </Text>
                          ))}
                        </div>
                      </Stack>
                    )}

                    {/* Sites Crawled List for SharePoint */}
                    {(selectedJob.completion_summary as any)?.sites_crawled_list && (selectedJob.completion_summary as any)?.sites_crawled_list.length > 0 && (
                      <Stack tokens={{ childrenGap: 8 }}>
                        <Text variant="medium" style={{ fontWeight: 600, color: theme.palette.themePrimary }}>
                          🌐 Sites Processed
                        </Text>
                        <div style={{ 
                          padding: '8px', 
                          backgroundColor: theme.palette.neutralLighterAlt, 
                          borderRadius: '4px',
                          border: `1px solid ${theme.palette.neutralLight}`,
                          maxHeight: '120px',
                          overflow: 'auto'
                        }}>
                          {(selectedJob.completion_summary as any)?.sites_crawled_list.map((site: string, index: number) => (
                            <Text key={index} variant="small" style={{ 
                              display: 'block', 
                              padding: '2px 0',
                              color: theme.palette.neutralPrimary
                            }}>
                              • {site}
                            </Text>
                          ))}
                        </div>
                      </Stack>
                    )}
                    
                    {/* Common Stats for SharePoint On-Premises */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginTop: '16px', paddingTop: '16px', borderTop: `1px solid ${theme.palette.neutralLight}` }}>
                      <Stack tokens={{ childrenGap: 4 }}>
                        <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>Run Count</Text>
                        <Text variant="mediumPlus" style={{ fontWeight: 600, color: '#0078d4' }}>
                          {selectedJob.runCount || 1}
                        </Text>
                      </Stack>
                      
                      <Stack tokens={{ childrenGap: 4 }}>
                        <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>Overall Success Rate</Text>
                        <Text variant="mediumPlus" style={{ fontWeight: 600, color: '#107c10' }}>
                          {selectedJob.successRate ? `${selectedJob.successRate}%` : 
                           (selectedJob.status as string) === 'completed' ? '100%' : 
                           (selectedJob.status === 'cancelled' || selectedJob.status === 'stopped') ? 'Stopped' : 'N/A'}
                        </Text>
                      </Stack>
                      
                      <Stack tokens={{ childrenGap: 4 }}>
                        <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>Last Run</Text>
                        <Text variant="mediumPlus" style={{ fontWeight: 600, color: '#666' }}>
                          {selectedJob.lastRun ? new Date(selectedJob.lastRun).toLocaleDateString() : 
                           selectedJob.created ? new Date(selectedJob.created).toLocaleDateString() : 'N/A'}
                        </Text>
                      </Stack>
                    </div>
                  </>
                )}
                
                {/* Public Web Connector Detailed Statistics */}
                {selectedJob.connectorType === 'public_website' && selectedJob.completion_summary && (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px' }}>
                      <Stack tokens={{ childrenGap: 4 }}>
                        <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>🌐 Sites Crawled</Text>
                        <Text variant="mediumPlus" style={{ fontWeight: 600, color: '#107c10' }}>
                          {((selectedJob.completion_summary as any)?.sites_crawled_count || 0).toLocaleString()}
                        </Text>
                      </Stack>
                      
                      <Stack tokens={{ childrenGap: 4 }}>
                        <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>📄 Documents Found</Text>
                        <Text variant="mediumPlus" style={{ fontWeight: 600, color: '#0078d4' }}>
                          {((selectedJob.completion_summary as any)?.documents_discovered || 0).toLocaleString()}
                        </Text>
                      </Stack>
                      
                      <Stack tokens={{ childrenGap: 4 }}>
                        <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>✅ Documents Indexed</Text>
                        <Text variant="mediumPlus" style={{ fontWeight: 600, color: '#8B5CF6' }}>
                          {((selectedJob.completion_summary as any)?.documents_indexed || 0).toLocaleString()}
                        </Text>
                      </Stack>
                      
                      <Stack tokens={{ childrenGap: 4 }}>
                        <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>🧩 Chunks Created</Text>
                        <Text variant="mediumPlus" style={{ fontWeight: 600, color: '#059669' }}>
                          {((selectedJob.completion_summary as any)?.chunks_created || 0).toLocaleString()}
                        </Text>
                      </Stack>
                    </div>
                    
                    {/* File Type Breakdown */}
                    {(selectedJob.completion_summary as any)?.file_type_stats && Object.keys((selectedJob.completion_summary as any)?.file_type_stats || {}).length > 0 && (
                      <Stack tokens={{ childrenGap: 8 }}>
                        <Text variant="medium" style={{ fontWeight: 600, color: theme.palette.themePrimary }}>
                          📁 File Type Breakdown
                        </Text>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '8px' }}>
                          {Object.entries((selectedJob.completion_summary as any)?.file_type_stats || {}).map(([fileType, count]) => (
                            <Stack key={fileType} tokens={{ childrenGap: 2 }} style={{ 
                              padding: '8px', 
                              backgroundColor: theme.palette.neutralLighterAlt, 
                              borderRadius: '4px',
                              border: `1px solid ${theme.palette.neutralLight}`
                            }}>
                              <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>
                                {fileType.toUpperCase()}
                              </Text>
                              <Text variant="medium" style={{ fontWeight: 600, color: theme.palette.themePrimary }}>
                                {(count as number).toLocaleString()}
                              </Text>
                            </Stack>
                          ))}
                        </div>
                      </Stack>
                    )}
                    
                    {/* Sites Crawled List */}
                    {(selectedJob.completion_summary as any)?.sites_crawled && (selectedJob.completion_summary as any)?.sites_crawled.length > 0 && (
                      <Stack tokens={{ childrenGap: 8 }}>
                        <Text variant="medium" style={{ fontWeight: 600, color: theme.palette.themePrimary }}>
                          🌐 Sites Crawled
                        </Text>
                        <div style={{ 
                          padding: '8px', 
                          backgroundColor: theme.palette.neutralLighterAlt, 
                          borderRadius: '4px',
                          border: `1px solid ${theme.palette.neutralLight}`,
                          maxHeight: '120px',
                          overflow: 'auto'
                        }}>
                          {(selectedJob.completion_summary as any)?.sites_crawled.map((site: string, index: number) => (
                            <Text key={index} variant="small" style={{ 
                              display: 'block', 
                              padding: '2px 0',
                              color: theme.palette.neutralPrimary
                            }}>
                              • {site}
                            </Text>
                          ))}
                        </div>
                      </Stack>
                    )}
                  </>
                )}
                
                {/* SharePoint 365 Detailed Statistics */}
                {(selectedJob.connectorType === 'sharepoint' || 
                  selectedJob.connectorType === 'sharepoint365' || 
                  selectedJob.connectorType === 'sharepoint_online' ||
                  selectedJob.connectorType === 'sharepoint-online' ||
                  getJobConnectorType(selectedJob) === 'Sharepoint Online') && (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px' }}>
                      <Stack tokens={{ childrenGap: 4 }}>
                        <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>📄 Documents Found</Text>
                        <Text variant="mediumPlus" style={{ fontWeight: 600, color: '#0078d4' }}>
                          {((selectedJob.completion_summary as any)?.documents_discovered || 
                            (selectedJob.completion_summary as any)?.documents_found || 
                            selectedJob.documentsProcessed || 0).toLocaleString()}
                        </Text>
                      </Stack>
                      
                      <Stack tokens={{ childrenGap: 4 }}>
                        <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>✅ Documents Processed</Text>
                        <Text variant="mediumPlus" style={{ fontWeight: 600, color: '#8B5CF6' }}>
                          {((selectedJob.completion_summary as any)?.documents_processed || 
                            selectedJob.documentsProcessed || 0).toLocaleString()}
                        </Text>
                      </Stack>
                      
                      <Stack tokens={{ childrenGap: 4 }}>
                        <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>📊 Documents Indexed</Text>
                        <Text variant="mediumPlus" style={{ fontWeight: 600, color: '#059669' }}>
                          {((selectedJob.completion_summary as any)?.documents_indexed || 
                            selectedJob.documentsProcessed || 0).toLocaleString()}
                        </Text>
                      </Stack>
                      
                      <Stack tokens={{ childrenGap: 4 }}>
                        <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>🧩 Chunks Created</Text>
                        <Text variant="mediumPlus" style={{ fontWeight: 600, color: '#F59E0B' }}>
                          {((selectedJob.completion_summary as any)?.chunks_created || 0).toLocaleString()}
                        </Text>
                      </Stack>
                    </div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px' }}>
                      <Stack tokens={{ childrenGap: 4 }}>
                        <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>🌐 Sites Crawled</Text>
                        <Text variant="mediumPlus" style={{ fontWeight: 600, color: '#107c10' }}>
                          {((selectedJob.completion_summary as any)?.sites_crawled_count || 
                            ((selectedJob.completion_summary as any)?.sites_crawled?.length) ||
                            ((selectedJob.completion_summary as any)?.sites_processed?.length) || 1).toLocaleString()}
                        </Text>
                      </Stack>
                      
                      <Stack tokens={{ childrenGap: 4 }}>
                        <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>🔒 ACL Entries</Text>
                        <Text variant="mediumPlus" style={{ fontWeight: 600, color: '#DC2626' }}>
                          {((selectedJob.completion_summary as any)?.acl_entries || 0).toLocaleString()}
                        </Text>
                      </Stack>
                      
                      <Stack tokens={{ childrenGap: 4 }}>
                        <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>❌ Processing Errors</Text>
                        <Text variant="mediumPlus" style={{ fontWeight: 600, color: '#DC2626' }}>
                          {((selectedJob.completion_summary as any)?.processing_errors || 0).toLocaleString()}
                        </Text>
                      </Stack>
                      
                      <Stack tokens={{ childrenGap: 4 }}>
                        <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>⏱️ Processing Time</Text>
                        <Text variant="mediumPlus" style={{ fontWeight: 600, color: '#6B7280' }}>
                          {(selectedJob.completion_summary as any)?.processing_duration || 
                           (selectedJob.completion_summary as any)?.execution_time ? 
                           `${Math.round((selectedJob.completion_summary as any).execution_time)}s` : 'N/A'}
                        </Text>
                      </Stack>
                    </div>

                    {/* File Type Breakdown for SharePoint */}
                    {(() => {
                      const fileTypes = (selectedJob.completion_summary as any)?.document_types || 
                                      (selectedJob.completion_summary as any)?.file_type_stats ||
                                      (selectedJob.completion_summary as any)?.file_types;
                      
                      // If no completion summary, try to extract from configuration
                      const configFileTypes = selectedJob.configuration?.file_types || 
                                             selectedJob.config?.configuration?.file_types ||
                                             selectedJob.config?.file_types;
                      
                      if (fileTypes && Object.keys(fileTypes).length > 0) {
                        return (
                          <Stack tokens={{ childrenGap: 8 }}>
                            <Text variant="medium" style={{ fontWeight: 600, color: theme.palette.themePrimary }}>
                              📁 File Types Processed
                            </Text>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '8px' }}>
                              {Object.entries(fileTypes).map(([fileType, count]) => (
                                <Stack key={fileType} tokens={{ childrenGap: 2 }} style={{ 
                                  padding: '8px', 
                                  backgroundColor: theme.palette.neutralLighterAlt, 
                                  borderRadius: '4px',
                                  border: `1px solid ${theme.palette.neutralLight}`
                                }}>
                                  <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>
                                    {fileType.toUpperCase().replace('.', '')}
                                  </Text>
                                  <Text variant="medium" style={{ fontWeight: 600, color: theme.palette.themePrimary }}>
                                    {(count as number).toLocaleString()}
                                  </Text>
                                </Stack>
                              ))}
                            </div>
                          </Stack>
                        );
                      } else if (configFileTypes && Array.isArray(configFileTypes)) {
                        return (
                          <Stack tokens={{ childrenGap: 8 }}>
                            <Text variant="medium" style={{ fontWeight: 600, color: theme.palette.themePrimary }}>
                              📁 Supported File Types
                            </Text>
                            <div style={{ 
                              padding: '12px', 
                              backgroundColor: theme.palette.neutralLighterAlt, 
                              borderRadius: '4px',
                              border: `1px solid ${theme.palette.neutralLight}`
                            }}>
                              <Text variant="small" style={{ color: theme.palette.neutralPrimary }}>
                                {configFileTypes.join(', ')}
                              </Text>
                            </div>
                          </Stack>
                        );
                      }
                      return null;
                    })()}

                    {/* Sites Crawled List */}
                    {(() => {
                      const sitesCrawled = (selectedJob.completion_summary as any)?.sites_crawled ||
                                         (selectedJob.completion_summary as any)?.sites_processed ||
                                         (selectedJob.completion_summary as any)?.sites_crawled_list;
                      
                      // Fallback to configuration URLs
                      const configSites = selectedJob.configuration?.site_urls ||
                                         selectedJob.config?.configuration?.site_urls ||
                                         selectedJob.configuration?.sourceUrls ||
                                         selectedJob.config?.configuration?.sourceUrls ||
                                         (selectedJob.configuration?.site_url ? [selectedJob.configuration.site_url] : []) ||
                                         (selectedJob.config?.configuration?.site_url ? [selectedJob.config.configuration.site_url] : []);
                      
                      const sitesToShow = sitesCrawled && sitesCrawled.length > 0 ? sitesCrawled : configSites;
                      
                      if (sitesToShow && sitesToShow.length > 0) {
                        return (
                          <Stack tokens={{ childrenGap: 8 }}>
                            <Text variant="medium" style={{ fontWeight: 600, color: theme.palette.themePrimary }}>
                              🌐 {sitesCrawled && sitesCrawled.length > 0 ? 'Sites Crawled' : 'Sites Configured'}
                            </Text>
                            <div style={{ 
                              padding: '12px', 
                              backgroundColor: theme.palette.neutralLighterAlt, 
                              borderRadius: '4px',
                              border: `1px solid ${theme.palette.neutralLight}`,
                              maxHeight: '150px',
                              overflow: 'auto'
                            }}>
                              {sitesToShow.map((site: string, index: number) => (
                                <Text key={index} variant="small" style={{ 
                                  display: 'block', 
                                  padding: '4px 0',
                                  color: theme.palette.neutralPrimary,
                                  fontFamily: 'monospace',
                                  fontSize: '12px',
                                  wordBreak: 'break-all'
                                }}>
                                  🔗 {site}
                                </Text>
                              ))}
                            </div>
                          </Stack>
                        );
                      }
                      return null;
                    })()}

                    {/* Common Stats for SharePoint Online */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginTop: '16px', paddingTop: '16px', borderTop: `1px solid ${theme.palette.neutralLight}` }}>
                      <Stack tokens={{ childrenGap: 4 }}>
                        <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>Run Count</Text>
                        <Text variant="mediumPlus" style={{ fontWeight: 600, color: '#0078d4' }}>
                          {selectedJob.runCount || 1}
                        </Text>
                      </Stack>
                      
                      <Stack tokens={{ childrenGap: 4 }}>
                        <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>Success Rate</Text>
                        <Text variant="mediumPlus" style={{ fontWeight: 600, color: '#107c10' }}>
                          {selectedJob.successRate ? `${selectedJob.successRate}%` : 
                           (selectedJob.status as string) === 'completed' ? '100%' : 
                           (selectedJob.status === 'cancelled' || selectedJob.status === 'stopped') ? 'Stopped' : 'N/A'}
                        </Text>
                      </Stack>
                      
                      <Stack tokens={{ childrenGap: 4 }}>
                        <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>Last Run</Text>
                        <Text variant="mediumPlus" style={{ fontWeight: 600, color: '#666' }}>
                          {selectedJob.lastRun ? new Date(selectedJob.lastRun).toLocaleDateString() : 
                           selectedJob.created ? new Date(selectedJob.created).toLocaleDateString() : 'N/A'}
                        </Text>
                      </Stack>
                    </div>
                  </>
                )}
                
                {/* General Statistics for Other Connectors */}
                {(!selectedJob.completion_summary || (
                    selectedJob.connectorType !== 'office365' && 
                    selectedJob.connectorType !== 'public_website' &&
                    selectedJob.connectorType !== 'sharepoint_onprem' &&
                    selectedJob.connectorType !== 'sharepoint-onprem' &&
                    selectedJob.connectorType !== 'sharepoint' &&
                    selectedJob.connectorType !== 'sharepoint365' &&
                    selectedJob.connectorType !== 'sharepoint_online' &&
                    selectedJob.connectorType !== 'sharepoint-online' &&
                    getJobConnectorType(selectedJob) !== 'Sharepoint Online'
                  )) && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                    <Stack tokens={{ childrenGap: 4 }}>
                      <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>Documents Processed</Text>
                      <Text variant="mediumPlus" style={{ fontWeight: 600, color: '#107c10' }}>
                        {selectedJob.documentsProcessed?.toLocaleString() || 
                         selectedJob.progress?.items_processed?.toLocaleString() || 
                         selectedJob.completion_summary?.documents_processed?.toLocaleString() || '0'}
                      </Text>
                    </Stack>
                    
                    <Stack tokens={{ childrenGap: 4 }}>
                      <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>Documents Indexed</Text>
                      <Text variant="mediumPlus" style={{ fontWeight: 600, color: '#0078d4' }}>
                        {selectedJob.completion_summary?.documents_indexed?.toLocaleString() || 
                         selectedJob.documentsProcessed?.toLocaleString() || '0'}
                      </Text>
                    </Stack>
                    
                    <Stack tokens={{ childrenGap: 4 }}>
                      <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>Success Rate</Text>
                      <Text variant="mediumPlus" style={{ fontWeight: 600, color: '#107c10' }}>
                        {selectedJob.successRate ? `${selectedJob.successRate}%` : 
                         (selectedJob.status as string) === 'completed' ? '100%' : 
                         (selectedJob.status === 'cancelled' || selectedJob.status === 'stopped') ? 'Stopped' : 'N/A'}
                      </Text>
                    </Stack>
                  </div>
                )}
                
                {/* Common Stats Row - Only for connectors without specific detailed stats */}
                {(selectedJob.connectorType !== 'office365' && 
                  selectedJob.connectorType !== 'public_website' &&
                  selectedJob.connectorType !== 'sharepoint_onprem' &&
                  selectedJob.connectorType !== 'sharepoint-onprem' &&
                  selectedJob.connectorType !== 'sharepoint' &&
                  selectedJob.connectorType !== 'sharepoint365' &&
                  selectedJob.connectorType !== 'sharepoint_online' &&
                  selectedJob.connectorType !== 'sharepoint-online' &&
                  getJobConnectorType(selectedJob) !== 'Sharepoint Online') && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                    <Stack tokens={{ childrenGap: 4 }}>
                      <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>Run Count</Text>
                      <Text variant="mediumPlus" style={{ fontWeight: 600, color: '#0078d4' }}>
                        {selectedJob.runCount || 0}
                      </Text>
                    </Stack>
                    
                    <Stack tokens={{ childrenGap: 4 }}>
                      <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>Success Rate</Text>
                      <Text variant="mediumPlus" style={{ fontWeight: 600, color: '#107c10' }}>
                        {selectedJob.successRate ? `${selectedJob.successRate}%` : 
                         (selectedJob.status as string) === 'completed' ? '100%' : 
                         (selectedJob.status === 'cancelled' || selectedJob.status === 'stopped') ? 'Stopped' : 'N/A'}
                      </Text>
                    </Stack>
                    
                    <Stack tokens={{ childrenGap: 4 }}>
                      <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>Processing Errors</Text>
                      <Text variant="mediumPlus" style={{ fontWeight: 600, color: selectedJob.completion_summary?.processing_errors ? '#DC2626' : '#107c10' }}>
                        {selectedJob.completion_summary?.processing_errors || 0}
                      </Text>
                    </Stack>
                  </div>
                )}
                
                {selectedJob.progress && (
                  <Stack tokens={{ childrenGap: 6 }}>
                    <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>Current Progress</Text>
                    <ProgressIndicator
                      percentComplete={
                        selectedJob.progress.total_items > 0 
                          ? selectedJob.progress.items_processed / selectedJob.progress.total_items
                          : 0
                      }
                      description={`${selectedJob.progress.items_processed} / ${selectedJob.progress.total_items} items processed`}
                    />
                    {selectedJob.progress.current_item && (
                      <Text variant="small" style={{ color: '#666', fontStyle: 'italic' }}>
                        {selectedJob.progress.phase}: {selectedJob.progress.current_item}
                      </Text>
                    )}
                  </Stack>
                )}
                
                {selectedJob.lastRun && (
                  <Stack tokens={{ childrenGap: 4 }}>
                    <Text variant="small" style={{ fontWeight: 500, color: '#666' }}>Last Run</Text>
                    <Text variant="small">{new Date(selectedJob.lastRun).toLocaleString()}</Text>
                  </Stack>
                )}
              </Stack>

              {/* Discovered Entities Section */}
              {(() => {
                console.log('selectedJob.discoveredEntities:', selectedJob.discoveredEntities);
                console.log('selectedJob keys:', Object.keys(selectedJob));
                return null;
              })()}
              {selectedJob.discoveredEntities && 
               Object.keys(selectedJob.discoveredEntities).length > 0 && (
                <>
                  <Separator />
                  <Stack tokens={{ childrenGap: 12 }}>
                    <Text variant="mediumPlus" style={{ fontWeight: 600, color: '#1a365d' }}>
                      🔍 Discovered Entities
                    </Text>
                    
                    {Object.entries(selectedJob.discoveredEntities).map(([category, entities]) => (
                      <Stack key={category} tokens={{ childrenGap: 8 }}>
                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                          <Text variant="medium" style={{ fontWeight: 500, color: '#0078d4' }}>
                            {String(category).charAt(0).toUpperCase() + String(category).slice(1).replace(/_/g, ' ')}
                          </Text>
                          <Text variant="small" style={{ 
                            backgroundColor: '#f3f2f1', 
                            padding: '2px 8px', 
                            borderRadius: '12px',
                            color: '#666',
                            fontSize: '11px'
                          }}>
                            {Array.isArray(entities) ? entities.length : 0} entities
                          </Text>
                        </Stack>
                        
                        <div style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '6px',
                          maxHeight: '120px',
                          overflowY: 'auto',
                          padding: '8px',
                          backgroundColor: '#fafafa',
                          borderRadius: '4px',
                          border: '1px solid #e1dfdd'
                        }}>
                          {Array.isArray(entities) && entities.slice(0, 50).map((entity, index) => (
                            <span key={index} style={{
                              backgroundColor: '#e8f4fd',
                              color: '#0078d4',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              border: '1px solid #c7e0f4',
                              whiteSpace: 'nowrap',
                              maxWidth: '200px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }} title={typeof entity === 'object' && entity ? `Count: ${entity.count}, Confidence: ${(entity.confidence * 100).toFixed(0)}%` : ''}>
                              {typeof entity === 'object' && entity ? entity.name : String(entity || '')}
                            </span>
                          ))}
                          {Array.isArray(entities) && entities.length > 50 && (
                            <span style={{
                              color: '#666',
                              fontStyle: 'italic',
                              fontSize: '11px',
                              padding: '4px 8px'
                            }}>
                              ... and {Array.isArray(entities) ? entities.length - 50 : 0} more
                            </span>
                          )}
                        </div>
                      </Stack>
                    ))}
                  </Stack>
                </>
              )}

              {/* Actions */}
              <Stack horizontal tokens={{ childrenGap: 12 }} style={{ paddingTop: '8px' }}>
                <PrimaryButton
                  text="View Logs"
                  iconProps={{ iconName: 'AlignLeft', styles: { root: { fontSize: '18px' } } }}
                  onClick={() => {
                    setShowJobDetailsPanel(false);
                    loadJobLogs(selectedJob.id);
                  }}
                  styles={{
                    root: { height: '42px' },
                    label: { fontSize: '15px' }
                  }}
                />
                <DefaultButton
                  text="Clear Logs"
                  iconProps={{ iconName: 'Delete', styles: { root: { fontSize: '18px' } } }}
                  onClick={() => clearJobLogs(selectedJob.id)}
                  styles={{
                    root: { 
                      backgroundColor: '#fff4e6', 
                      borderColor: '#ff8c00',
                      color: '#ff8c00',
                      height: '42px'
                    },
                    rootHovered: { 
                      backgroundColor: '#ffe6cc',
                      borderColor: '#e07600',
                      color: '#e07600'
                    },
                    label: { fontSize: '15px' }
                  }}
                />
                <DefaultButton
                  text="Close"
                  onClick={() => setShowJobDetailsPanel(false)}
                  styles={{
                    root: { height: '42px' },
                    label: { fontSize: '15px' }
                  }}
                />
              </Stack>
            </Stack>
            );
          })()}
        </Panel>

        {/* Job Logs Panel */}
        <Panel
          isOpen={showLogsPanel}
          onDismiss={() => setShowLogsPanel(false)}
          headerText={`Job Logs - ${selectedJob ? getJobDisplayName(selectedJob) : 'Unknown Job'}`}
          type={PanelType.large}
          className={styles.logsPanel}
        >
          <Stack tokens={{ childrenGap: 16 }}>
            {logsLoading ? (
              <Spinner size={SpinnerSize.large} label="Loading logs..." />
            ) : (
              <Stack tokens={{ childrenGap: 12 }}>
                {selectedJob && (
                  <Stack tokens={{ childrenGap: 8 }}>
                    <Text variant="medium" style={{ fontWeight: 600 }}>
                      Job: {getJobDisplayName(selectedJob)} ({selectedJob.id})
                    </Text>
                    <Text variant="small" style={{ color: '#666' }}>
                      Status: {selectedJob.status} | Last updated: {new Date().toLocaleString()}
                    </Text>
                  </Stack>
                )}
                
                <div className={styles.logContainer}>
                  {jobLogs ? (
                    (() => {
                      // Check if logs are structured objects or plain strings (backward compatibility)
                      const logs = Array.isArray(jobLogs) ? jobLogs : 
                                  typeof jobLogs === 'string' ? jobLogs.split('\n') : [];
                      
                      return logs.map((logEntry, index) => {
                        let timestamp = '';
                        let level = 'INFO';
                        let message = '';
                        
                        // Handle structured log objects
                        if (typeof logEntry === 'object' && logEntry.timestamp) {
                          timestamp = logEntry.timestamp;
                          level = logEntry.level || 'INFO';
                          message = logEntry.message || '';
                        } else {
                          // Handle plain string logs (backward compatibility)
                          const line = typeof logEntry === 'string' ? logEntry : String(logEntry);
                          const timestampMatch = line.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{6})/);
                          const levelMatch = line.match(/\[(INFO|ERROR|WARNING|DEBUG)\]/);
                          
                          timestamp = timestampMatch ? timestampMatch[1] : '';
                          level = levelMatch ? levelMatch[1] : 'INFO';
                          message = line.replace(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{6}\s*/, '')
                                       .replace(/\[(INFO|ERROR|WARNING|DEBUG)\]\s*/, '');
                        }
                        
                        // Format timestamp for display
                        const displayTime = timestamp ? 
                          new Date(timestamp).toLocaleString('en-US', {
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit',
                            hour12: false
                          }) : '';
                        
                        // Color coding for log levels
                        let logColor = '#d4d4d4';
                        let backgroundColor = 'transparent';
                        let borderColor = 'transparent';
                        
                        switch (level) {
                          case 'ERROR':
                            logColor = '#ff6b6b';
                            backgroundColor = 'rgba(255, 107, 107, 0.05)';
                            borderColor = '#ff6b6b';
                            break;
                          case 'WARNING':
                            logColor = '#ffd93d';
                            backgroundColor = 'rgba(255, 217, 61, 0.05)';
                            borderColor = '#ffd93d';
                            break;
                          case 'INFO':
                            logColor = '#74c0fc';
                            backgroundColor = 'rgba(116, 192, 252, 0.05)';
                            borderColor = '#74c0fc';
                            break;
                          case 'DEBUG':
                            logColor = '#a7a7a7';
                            backgroundColor = 'rgba(167, 167, 167, 0.05)';
                            borderColor = '#a7a7a7';
                            break;
                        }
                        
                        return (
                          <div 
                            key={index} 
                            style={{ 
                              marginBottom: '4px',
                              padding: '6px 10px',
                              backgroundColor: backgroundColor,
                              borderLeft: `3px solid ${borderColor}`,
                              borderRadius: '3px',
                              fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                              fontSize: '12px',
                              lineHeight: '1.4'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              {displayTime && (
                                <span style={{ 
                                  color: '#888', 
                                  fontSize: '11px',
                                  minWidth: '130px',
                                  fontWeight: '500'
                                }}>
                                  {displayTime}
                                </span>
                              )}
                              <span style={{ 
                                color: logColor, 
                                fontWeight: 'bold',
                                padding: '2px 6px',
                                backgroundColor: logColor.replace(')', ', 0.15)').replace('rgb', 'rgba'),
                                borderRadius: '3px',
                                fontSize: '10px',
                                minWidth: '50px',
                                textAlign: 'center',
                                textTransform: 'uppercase'
                              }}>
                                {level}
                              </span>
                              <span style={{ 
                                color: '#e6e6e6',
                                flex: 1,
                                wordBreak: 'break-word'
                              }}>
                                {message}
                              </span>
                            </div>
                          </div>
                        );
                      });
                    })()
                  ) : (
                    <div style={{ color: '#888', fontStyle: 'italic', textAlign: 'center', padding: '20px' }}>
                      No logs available for this job
                    </div>
                  )}
                </div>
              </Stack>
            )}
            <Stack horizontal tokens={{ childrenGap: 12 }} horizontalAlign="end">
              <DefaultButton
                text="Clear Logs"
                iconProps={{ iconName: 'Delete', styles: { root: { fontSize: '18px' } } }}
                onClick={() => {
                  if (selectedJob) {
                    clearJobLogs(selectedJob.id);
                  }
                }}
                disabled={logsLoading}
                styles={{
                  root: { 
                    backgroundColor: '#fff4e6', 
                    borderColor: '#ff8c00',
                    color: '#ff8c00',
                    height: '42px'
                  },
                  rootHovered: { 
                    backgroundColor: '#ffe6cc',
                    borderColor: '#e07600',
                    color: '#e07600'
                  },
                  label: { fontSize: '15px' }
                }}
              />
              <DefaultButton
                text="Refresh"
                iconProps={{ iconName: 'Refresh', styles: { root: { fontSize: '18px' } } }}
                onClick={() => {
                  if (selectedJob) {
                    loadJobLogs(selectedJob.id);
                  }
                }}
                disabled={logsLoading}
                styles={{
                  root: { height: '42px' },
                  label: { fontSize: '15px' }
                }}
              />
              <DefaultButton
                text="Close"
                onClick={() => setShowLogsPanel(false)}
                styles={{
                  root: { height: '42px' },
                  label: { fontSize: '15px' }
                }}
              />
            </Stack>
          </Stack>
        </Panel>

        {/* Edit Job Panel */}
        <Panel
          isOpen={showEditJobPanel}
          onDismiss={() => setShowEditJobPanel(false)}
          headerText={`Edit Job - ${editingJob ? getJobDisplayName(editingJob) : 'Unknown Job'}`}
          type={PanelType.large}
          className={styles.jobDetailsPanel}
        >
          {editingJob && (() => {
            const theme = getTheme();
            
            // File type options for SharePoint connector
            const fileTypeOptions: IDropdownOption[] = [
              { key: '.pdf', text: 'PDF Documents (.pdf)' },
              { key: '.docx', text: 'Word Documents (.docx)' },
              { key: '.doc', text: 'Word Documents (.doc)' },
              { key: '.pptx', text: 'PowerPoint (.pptx)' },
              { key: '.ppt', text: 'PowerPoint (.ppt)' },
              { key: '.xlsx', text: 'Excel Workbooks (.xlsx)' },
              { key: '.xls', text: 'Excel Workbooks (.xls)' },
              { key: '.txt', text: 'Text Files (.txt)' },
              { key: '.html', text: 'HTML Files (.html)' },
              { key: '.htm', text: 'HTML Files (.htm)' },
              { key: '.rtf', text: 'Rich Text Format (.rtf)' },
              { key: '.csv', text: 'CSV Files (.csv)' }
            ];

            return (
              <Stack tokens={{ childrenGap: 24 }} styles={{ root: { padding: '0 4px' } }}>
                {/* Header Section */}
                <Stack tokens={{ childrenGap: 8 }}>
                  <Text variant="xLarge" style={{ fontWeight: 600, color: '#1a365d' }}>
                    📝 Edit Job Configuration
                  </Text>
                  <Text style={{ color: '#64748b' }}>
                    Modify job settings and configuration. Changes will be applied immediately.
                  </Text>
                  <MessageBar messageBarType={MessageBarType.info} isMultiline={false}>
                    💡 Some changes may require a job restart to take effect
                  </MessageBar>
                </Stack>

                {/* Basic Configuration Section */}
                <Stack tokens={{ childrenGap: 12 }}>
                  <Text variant="mediumPlus" style={{ fontWeight: 600, color: theme.palette.themePrimary }}>
                    📋 Basic Configuration
                  </Text>
                  <Separator styles={{ root: { margin: '2px 0' } }} />
                  
                  <TextField
                    label="Job Name"
                    value={editFormData.name}
                    onChange={(_, value) => setEditFormData({ ...editFormData, name: value || '' })}
                    placeholder="Enter descriptive job name"
                    required
                    description="A clear, descriptive name for this crawling job"
                  />
                  
                  <TextField
                    label="Description"
                    value={editFormData.description}
                    onChange={(_, value) => setEditFormData({ ...editFormData, description: value || '' })}
                    placeholder="Describe what this job does and its purpose"
                    multiline
                    rows={3}
                    description="Optional description to help identify the job's purpose"
                  />
                  
                  <TextField
                    label="Target Index"
                    value={editFormData.targetIndex}
                    onChange={(_, value) => setEditFormData({ ...editFormData, targetIndex: value || '' })}
                    placeholder="azure-search-index-name"
                    required
                    description="The Azure AI Search index where documents will be stored"
                  />
                </Stack>

                {/* SharePoint On-Premises Configuration */}
                {(editingJob.connectorType === 'sharepoint_onprem' || editingJob.connectorType === 'sharepoint-onprem') && (
                  <>
                    {/* Content Source Section */}
                    <Stack tokens={{ childrenGap: 12 }}>
                      <Text variant="mediumPlus" style={{ fontWeight: 600, color: theme.palette.themePrimary }}>
                        🔗 SharePoint On-Premises Source
                      </Text>
                      <Separator styles={{ root: { margin: '2px 0' } }} />
                      
                      <TextField
                        label="SharePoint Server URL"
                        value={editFormData.site_url || (Array.isArray(editFormData.sites) ? editFormData.sites[0] : '')}
                        onChange={(_, value) => {
                          const newSites = value ? [value] : [];
                          setEditFormData({ 
                            ...editFormData, 
                            site_url: value || '',
                            sites: newSites,
                            configuration: { 
                              ...editFormData.configuration, 
                              sites: newSites,
                              site_url: value || ''
                            }
                          });
                        }}
                        placeholder="http://sharepoint.company.com or http://sharepoint.company.com/sites/sitename"
                        required
                        description="SharePoint On-Premises server URL or specific site URL"
                      />

                      {/* Authentication Section */}
                      <Text variant="mediumPlus" style={{ fontWeight: 600, color: theme.palette.themePrimary, marginTop: 16 }}>
                        🔐 Authentication
                      </Text>
                      <Separator styles={{ root: { margin: '2px 0' } }} />
                      
                      <TextField
                        label="Username"
                        value={editFormData.username || ''}
                        onChange={(_, value) => setEditFormData({ 
                          ...editFormData, 
                          username: value || '',
                          configuration: { ...editFormData.configuration, username: value || '' }
                        })}
                        placeholder="domain\\username or username@domain.com"
                        required
                        description="SharePoint On-Premises username"
                      />
                      
                      <TextField
                        label="Password"
                        type="password"
                        value={editFormData.password || ''}
                        onChange={(_, value) => setEditFormData({ 
                          ...editFormData, 
                          password: value || '',
                          configuration: { ...editFormData.configuration, password: value || '' }
                        })}
                        placeholder="Enter password"
                        required
                        description="Password for SharePoint authentication"
                      />
                      
                      <TextField
                        label="Domain (Optional)"
                        value={editFormData.domain || ''}
                        onChange={(_, value) => setEditFormData({ 
                          ...editFormData, 
                          domain: value || '',
                          configuration: { ...editFormData.configuration, domain: value || '' }
                        })}
                        placeholder="COMPANY or company.com"
                        description="Windows domain name (leave empty if using username@domain.com format)"
                      />

                      {/* Crawl Configuration */}
                      <Text variant="mediumPlus" style={{ fontWeight: 600, color: theme.palette.themePrimary, marginTop: 16 }}>
                        🕷️ Crawl Settings
                      </Text>
                      <Separator styles={{ root: { margin: '2px 0' } }} />
                      
                      <Toggle
                        label="Crawl Subsites"
                        checked={editFormData.crawl_subsites !== false}
                        onChange={(_, checked) => setEditFormData({ 
                          ...editFormData, 
                          crawl_subsites: checked,
                          configuration: { ...editFormData.configuration, crawl_subsites: checked }
                        })}
                        onText="Enabled"
                        offText="Disabled"
                      />
                      
                      <Toggle
                        label="Include ACL (Permissions)"
                        checked={editFormData.include_acl !== false}
                        onChange={(_, checked) => setEditFormData({ 
                          ...editFormData, 
                          include_acl: checked,
                          configuration: { ...editFormData.configuration, include_acl: checked }
                        })}
                        onText="Enabled"
                        offText="Disabled"
                      />

                      <Dropdown
                        label="Content Types to Crawl"
                        multiSelect
                        options={[
                          { key: 'documents', text: '📄 Documents' },
                          { key: 'folders', text: '📁 Folders' },
                          { key: 'lists', text: '📋 Lists' },
                          { key: 'pages', text: '📄 Pages' }
                        ]}
                        selectedKeys={editFormData.content_types || ['documents', 'folders', 'lists', 'pages']}
                        onChange={(_, option) => {
                          if (option) {
                            const currentTypes = editFormData.content_types || [];
                            const newTypes = option.selected 
                              ? [...currentTypes, option.key as string]
                              : currentTypes.filter(type => type !== option.key);
                            setEditFormData({ 
                              ...editFormData, 
                              content_types: newTypes,
                              configuration: { ...editFormData.configuration, content_types: newTypes }
                            });
                          }
                        }}
                        placeholder="Select content types to crawl"
                      />
                    </Stack>
                  </>
                )}

                {/* SharePoint Online Configuration */}
                {(editingJob.connectorType === 'sharepoint_online' || editingJob.connectorType === 'sharepoint') && (
                  <>
                    {/* Content Source Section */}
                    <Stack tokens={{ childrenGap: 12 }}>
                      <Text variant="mediumPlus" style={{ fontWeight: 600, color: theme.palette.themePrimary }}>
                        🔗 Content Source
                      </Text>
                      <Separator styles={{ root: { margin: '2px 0' } }} />
                      
                      <TextField
                        label="SharePoint Site URL"
                        value={editFormData.site_url}
                        onChange={(_, value) => setEditFormData({ 
                          ...editFormData, 
                          site_url: value || '',
                          configuration: { ...editFormData.configuration, site_url: value || '' }
                        })}
                        placeholder="https://yourcompany.sharepoint.com/sites/sitename"
                        required
                        description="Main SharePoint site URL to crawl"
                      />

                      {/* Multiple Site URLs for Discovery */}
                      <TextField
                        label="Additional Site URLs (Optional)"
                        value={Array.isArray(editFormData.site_urls) ? editFormData.site_urls.filter(url => url !== editFormData.site_url).join('\n') : ''}
                        onChange={(_, value) => {
                          const additionalUrls = value ? value.split('\n').map(url => url.trim()).filter(url => url) : [];
                          const allUrls = editFormData.site_url ? [editFormData.site_url, ...additionalUrls] : additionalUrls;
                          setEditFormData({ 
                            ...editFormData, 
                            site_urls: allUrls,
                            configuration: { ...editFormData.configuration, site_urls: allUrls }
                          });
                        }}
                        placeholder="https://yourcompany.sharepoint.com/sites/site2&#10;https://yourcompany.sharepoint.com/sites/site3"
                        multiline
                        rows={3}
                        description="Additional SharePoint sites to discover and crawl (one per line)"
                      />

                      <TextField
                        label="Document Libraries (Optional)"
                        value={Array.isArray(editFormData.libraries) ? editFormData.libraries.join(', ') : ''}
                        onChange={(_, value) => {
                          const libraries = value ? value.split(',').map(lib => lib.trim()).filter(lib => lib) : [];
                          setEditFormData({ 
                            ...editFormData, 
                            libraries,
                            configuration: { ...editFormData.configuration, libraries }
                          });
                        }}
                        placeholder="Documents, Shared Documents, Archive"
                        description="Specific libraries to crawl (leave empty to crawl all libraries)"
                        multiline
                        rows={2}
                      />
                    </Stack>

                    {/* Content Discovery Section */}
                    <Stack tokens={{ childrenGap: 12 }}>
                      <Text variant="mediumPlus" style={{ fontWeight: 600, color: theme.palette.themePrimary }}>
                        🔍 Content Discovery
                      </Text>
                      <Separator styles={{ root: { margin: '4px 0' } }} />
                      
                      <Stack horizontal tokens={{ childrenGap: 16 }}>
                        <Toggle
                          label="Crawl Subsites"
                          checked={editFormData.crawl_subsites || editFormData.configuration?.crawl_subsites || false}
                          onChange={(_, checked) => setEditFormData({ 
                            ...editFormData, 
                            crawl_subsites: checked || false,
                            configuration: { ...editFormData.configuration, crawl_subsites: checked || false }
                          })}
                          onText="Yes"
                          offText="No"
                        />
                        <Toggle
                          label="Enable ACL Extraction"
                          checked={editFormData.enable_acl || editFormData.configuration?.enable_acl || false}
                          onChange={(_, checked) => setEditFormData({ 
                            ...editFormData, 
                            enable_acl: checked || false,
                            configuration: { ...editFormData.configuration, enable_acl: checked || false }
                          })}
                          onText="Yes"
                          offText="No"
                        />
                      </Stack>

                      <Stack tokens={{ childrenGap: 4 }}>
                        <Dropdown
                          label="File Types to Include"
                          multiSelect
                          options={fileTypeOptions}
                          selectedKeys={editFormData.file_types || editFormData.configuration?.file_types || ['.pdf', '.docx', '.pptx', '.html', '.msg', '.aspx']}
                          onChange={(_, option) => {
                            if (option) {
                              const currentFileTypes = editFormData.file_types || editFormData.configuration?.file_types || [];
                              const newFileTypes = option.selected 
                                ? [...currentFileTypes, option.key as string]
                                : currentFileTypes.filter(type => type !== option.key);
                              setEditFormData({ 
                                ...editFormData, 
                                file_types: newFileTypes,
                                configuration: { ...editFormData.configuration, file_types: newFileTypes }
                              });
                            }
                          }}
                          placeholder="Select file types to crawl"
                        />
                        <Text variant="small" style={{ color: theme.palette.neutralSecondary }}>
                          Choose which document types to include in the crawl
                        </Text>
                      </Stack>

                      <TextField
                        label="Maximum Documents (Optional)"
                        type="number"
                        value={editFormData.max_documents?.toString() || editFormData.configuration?.max_documents?.toString() || ''}
                        onChange={(_, value) => {
                          const maxDocs = value && !isNaN(parseInt(value)) ? parseInt(value) : undefined;
                          setEditFormData({ 
                            ...editFormData, 
                            max_documents: maxDocs,
                            configuration: { ...editFormData.configuration, max_documents: maxDocs }
                          });
                        }}
                        placeholder="1000"
                        description="Limit the number of documents to process (leave empty for no limit)"
                      />
                    </Stack>

                    {/* AI Processing Section */}
                    <Stack tokens={{ childrenGap: 12 }}>
                      <Text variant="mediumPlus" style={{ fontWeight: 600, color: theme.palette.themePrimary }}>
                        🤖 AI Processing
                      </Text>
                      <Separator styles={{ root: { margin: '4px 0' } }} />
                      
                      <Stack horizontal tokens={{ childrenGap: 12 }} wrap>
                        <Toggle
                          label="Enable Vector Embeddings"
                          checked={editFormData.enable_embeddings ?? editFormData.configuration?.enable_embeddings ?? true}
                          onChange={(_, checked) => setEditFormData({ 
                            ...editFormData, 
                            enable_embeddings: checked || false,
                            configuration: { ...editFormData.configuration, enable_embeddings: checked || false }
                          })}
                          onText="Yes"
                          offText="No"
                        />
                      </Stack>

                      {(editFormData.enable_embeddings ?? editFormData.configuration?.enable_embeddings ?? true) && (
                        <Stack tokens={{ childrenGap: 12 }}>
                          <Dropdown
                            label="Embedding Model"
                            options={[
                              { key: 'text-embedding-3-small', text: 'text-embedding-3-small (Faster, Lower Cost)' },
                              { key: 'text-embedding-3-large', text: 'text-embedding-3-large (Higher Quality)' },
                              { key: 'text-embedding-ada-002', text: 'text-embedding-ada-002 (Legacy)' }
                            ]}
                            selectedKey={editFormData.embedding_model || editFormData.configuration?.embedding_model || 'text-embedding-3-large'}
                            onChange={(_, option) => setEditFormData({ 
                              ...editFormData, 
                              embedding_model: option?.key as string || 'text-embedding-3-large',
                              configuration: { ...editFormData.configuration, embedding_model: option?.key as string || 'text-embedding-3-large' }
                            })}
                          />

                          <Stack horizontal tokens={{ childrenGap: 16 }}>
                            <TextField
                              label="Chunk Size"
                              type="number"
                              value={(editFormData.chunk_size || editFormData.configuration?.chunk_size || 2000).toString()}
                              onChange={(_, value) => {
                                const chunkSize = value && !isNaN(parseInt(value)) ? parseInt(value) : 2000;
                                setEditFormData({ 
                                  ...editFormData, 
                                  chunk_size: chunkSize,
                                  configuration: { ...editFormData.configuration, chunk_size: chunkSize }
                                });
                              }}
                              placeholder="2000"
                              description="Characters per text chunk"
                              styles={{ root: { width: '50%' } }}
                            />
                            <TextField
                              label="Chunk Overlap"
                              type="number"
                              value={(editFormData.chunk_overlap || editFormData.configuration?.chunk_overlap || 200).toString()}
                              onChange={(_, value) => {
                                const chunkOverlap = value && !isNaN(parseInt(value)) ? parseInt(value) : 200;
                                setEditFormData({ 
                                  ...editFormData, 
                                  chunk_overlap: chunkOverlap,
                                  configuration: { ...editFormData.configuration, chunk_overlap: chunkOverlap }
                                });
                              }}
                              placeholder="200"
                              description="Character overlap between chunks"
                              styles={{ root: { width: '50%' } }}
                            />
                          </Stack>
                        </Stack>
                      )}
                    </Stack>

                    {/* Advanced Settings Section */}
                    <Stack tokens={{ childrenGap: 12 }}>
                      <Text variant="mediumPlus" style={{ fontWeight: 600, color: theme.palette.themePrimary }}>
                        ⚙️ Advanced Settings
                      </Text>
                      <Separator styles={{ root: { margin: '4px 0' } }} />
                      
                      <Stack horizontal tokens={{ childrenGap: 16 }}>
                        <Toggle
                          label="Enable File Age Filter"
                          checked={editFormData.max_file_age_enabled ?? editFormData.configuration?.max_file_age_enabled ?? false}
                          onChange={(_, checked) => setEditFormData({ 
                            ...editFormData, 
                            max_file_age_enabled: checked || false,
                            configuration: { ...editFormData.configuration, max_file_age_enabled: checked || false }
                          })}
                          onText="Yes"
                          offText="No"
                        />
                        
                        {(editFormData.max_file_age_enabled ?? editFormData.configuration?.max_file_age_enabled ?? false) && (
                          <TextField
                            label="Max File Age (Days)"
                            type="number"
                            value={(editFormData.max_file_age_days || editFormData.configuration?.max_file_age_days || 90).toString()}
                            onChange={(_, value) => {
                              const maxAge = value && !isNaN(parseInt(value)) ? parseInt(value) : 90;
                              setEditFormData({ 
                                ...editFormData, 
                                max_file_age_days: maxAge,
                                configuration: { ...editFormData.configuration, max_file_age_days: maxAge }
                              });
                            }}
                            placeholder="90"
                            description="Skip files older than this many days"
                            styles={{ root: { width: '200px' } }}
                          />
                        )}
                      </Stack>
                    </Stack>
                  </>
                )}

                {/* Public Web Connector Configuration */}
                {editingJob.connectorType === 'public_website' && (
                  <>
                    {/* Web Crawling Source Section */}
                    <Stack tokens={{ childrenGap: 12 }}>
                      <Text variant="mediumPlus" style={{ fontWeight: 600, color: theme.palette.themePrimary }}>
                        🌐 Web Crawling Source
                      </Text>
                      <Separator styles={{ root: { margin: '2px 0' } }} />
                      
                      <TextField
                        label="Start URLs"
                        value={Array.isArray(editFormData.start_urls) ? editFormData.start_urls.join('\n') : (editFormData.configuration?.start_urls || []).join('\n')}
                        onChange={(_, value) => {
                          const startUrls = value ? value.split('\n').map(url => url.trim()).filter(url => url) : [];
                          setEditFormData({ 
                            ...editFormData, 
                            start_urls: startUrls,
                            configuration: { ...editFormData.configuration, start_urls: startUrls }
                          });
                        }}
                        placeholder="https://example.com&#10;https://example.com/page1&#10;https://example.com/page2"
                        multiline
                        rows={4}
                        required
                        description="URLs to start crawling from (one per line)"
                      />

                      <TextField
                        label="Allowed Domains (Optional)"
                        value={Array.isArray(editFormData.allowed_domains) ? editFormData.allowed_domains.join('\n') : (editFormData.configuration?.allowed_domains || []).join('\n')}
                        onChange={(_, value) => {
                          const allowedDomains = value ? value.split('\n').map(domain => domain.trim()).filter(domain => domain) : [];
                          setEditFormData({ 
                            ...editFormData, 
                            allowed_domains: allowedDomains,
                            configuration: { ...editFormData.configuration, allowed_domains: allowedDomains }
                          });
                        }}
                        placeholder="example.com&#10;subdomain.example.com"
                        multiline
                        rows={3}
                        description="Restrict crawling to specific domains (leave empty to allow all domains from start URLs)"
                      />
                    </Stack>

                    {/* Crawling Behavior Section */}
                    <Stack tokens={{ childrenGap: 12 }}>
                      <Text variant="mediumPlus" style={{ fontWeight: 600, color: theme.palette.themePrimary }}>
                        🔍 Crawling Behavior
                      </Text>
                      <Separator styles={{ root: { margin: '2px 0' } }} />
                      
                      <Stack horizontal tokens={{ childrenGap: 16 }}>
                        <TextField
                          label="Max Depth"
                          type="number"
                          value={(editFormData.max_depth || editFormData.configuration?.max_depth || 2).toString()}
                          onChange={(_, value) => {
                            const maxDepth = value && !isNaN(parseInt(value)) ? parseInt(value) : 2;
                            setEditFormData({
                              ...editFormData,
                              max_depth: maxDepth,
                              configuration: { ...editFormData.configuration, max_depth: maxDepth }
                            });
                          }}
                          placeholder="2"
                          description="How many link levels deep to crawl"
                          styles={{ root: { width: '50%' } }}
                        />
                        <TextField
                          label="Max Pages"
                          type="number"
                          value={(editFormData.max_pages || editFormData.configuration?.max_pages || 100).toString()}
                          onChange={(_, value) => {
                            const maxPages = value && !isNaN(parseInt(value)) ? parseInt(value) : 100;
                            setEditFormData({
                              ...editFormData,
                              max_pages: maxPages,
                              configuration: { ...editFormData.configuration, max_pages: maxPages }
                            });
                          }}
                          placeholder="100"
                          description="Maximum number of pages to crawl"
                          styles={{ root: { width: '50%' } }}
                        />
                      </Stack>

                      <Stack horizontal tokens={{ childrenGap: 16 }}>
                        <TextField
                          label="Crawl Delay (seconds)"
                          type="number"
                          value={(editFormData.crawl_delay || editFormData.configuration?.crawl_delay || 1).toString()}
                          onChange={(_, value) => {
                            const crawlDelay = value && !isNaN(parseInt(value)) ? parseInt(value) : 1;
                            setEditFormData({
                              ...editFormData,
                              crawl_delay: crawlDelay,
                              configuration: { ...editFormData.configuration, crawl_delay: crawlDelay }
                            });
                          }}
                          placeholder="1"
                          description="Delay between requests to be respectful"
                          styles={{ root: { width: '50%' } }}
                        />
                        <TextField
                          label="Max File Size (MB)"
                          type="number"
                          value={(editFormData.max_file_size_mb || editFormData.configuration?.max_file_size_mb || 10).toString()}
                          onChange={(_, value) => {
                            const maxFileSize = value && !isNaN(parseInt(value)) ? parseInt(value) : 10;
                            setEditFormData({
                              ...editFormData,
                              max_file_size_mb: maxFileSize,
                              configuration: { ...editFormData.configuration, max_file_size_mb: maxFileSize }
                            });
                          }}
                          placeholder="10"
                          description="Maximum size for downloaded files"
                          styles={{ root: { width: '50%' } }}
                        />
                      </Stack>

                      <Stack horizontal tokens={{ childrenGap: 12 }} wrap>
                        <Toggle
                          label="Respect robots.txt"
                          checked={editFormData.respect_robots_txt ?? editFormData.configuration?.respect_robots_txt ?? true}
                          onChange={(_, checked) => setEditFormData({
                            ...editFormData,
                            respect_robots_txt: checked || false,
                            configuration: { ...editFormData.configuration, respect_robots_txt: checked || false }
                          })}
                          onText="Yes"
                          offText="No"
                        />
                        <Toggle
                          label="Follow Redirects"
                          checked={editFormData.follow_redirects ?? editFormData.configuration?.follow_redirects ?? true}
                          onChange={(_, checked) => setEditFormData({
                            ...editFormData,
                            follow_redirects: checked || false,
                            configuration: { ...editFormData.configuration, follow_redirects: checked || false }
                          })}
                          onText="Yes"
                          offText="No"
                        />
                        <Toggle
                          label="Enable JavaScript"
                          checked={editFormData.enable_javascript ?? editFormData.configuration?.enable_javascript ?? true}
                          onChange={(_, checked) => setEditFormData({
                            ...editFormData,
                            enable_javascript: checked || false,
                            configuration: { ...editFormData.configuration, enable_javascript: checked || false }
                          })}
                          onText="Yes"
                          offText="No"
                        />
                      </Stack>

                      <Stack tokens={{ childrenGap: 4 }}>
                        <Dropdown
                          label="Content Types to Include"
                          multiSelect
                          options={[
                            { key: 'text/html', text: 'HTML Pages' },
                            { key: 'application/pdf', text: 'PDF Documents' },
                            { key: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', text: 'Word Documents (.docx)' },
                            { key: 'application/vnd.openxmlformats-officedocument.presentationml.presentation', text: 'PowerPoint Files (.pptx)' },
                            { key: 'text/plain', text: 'Text Files' }
                          ]}
                          selectedKeys={editFormData.content_types || editFormData.configuration?.content_types || ['text/html', 'application/pdf']}
                          onChange={(_, option) => {
                            if (option) {
                              const currentContentTypes = editFormData.content_types || editFormData.configuration?.content_types || [];
                              const newContentTypes = option.selected 
                                ? [...currentContentTypes, option.key as string]
                                : currentContentTypes.filter(type => type !== option.key);
                              setEditFormData({ 
                                ...editFormData, 
                                content_types: newContentTypes,
                                configuration: { ...editFormData.configuration, content_types: newContentTypes }
                              });
                            }
                          }}
                          placeholder="Select content types to crawl"
                        />
                        <Text variant="small" style={{ color: theme.palette.neutralSecondary }}>
                          Choose which content types to include in the crawl
                        </Text>
                      </Stack>
                    </Stack>

                    {/* URL Filtering Section */}
                    <Stack tokens={{ childrenGap: 12 }}>
                      <Text variant="mediumPlus" style={{ fontWeight: 600, color: theme.palette.themePrimary }}>
                        🎯 URL Filtering
                      </Text>
                      <Separator styles={{ root: { margin: '2px 0' } }} />
                      
                      <TextField
                        label="Include Paths (Optional)"
                        value={Array.isArray(editFormData.include_paths) ? editFormData.include_paths.join('\n') : (editFormData.configuration?.include_paths || []).join('\n')}
                        onChange={(_, value) => {
                          const includePaths = value ? value.split('\n').map(path => path.trim()).filter(path => path) : [];
                          setEditFormData({ 
                            ...editFormData, 
                            include_paths: includePaths,
                            configuration: { ...editFormData.configuration, include_paths: includePaths }
                          });
                        }}
                        placeholder="/docs/&#10;/articles/&#10;/blog/"
                        multiline
                        rows={2}
                        description="Only crawl URLs containing these paths (leave empty to include all paths)"
                      />

                      <TextField
                        label="Exclude Paths (Optional)"
                        value={Array.isArray(editFormData.exclude_paths) ? editFormData.exclude_paths.join('\n') : (editFormData.configuration?.exclude_paths || []).join('\n')}
                        onChange={(_, value) => {
                          const excludePaths = value ? value.split('\n').map(path => path.trim()).filter(path => path) : [];
                          setEditFormData({ 
                            ...editFormData, 
                            exclude_paths: excludePaths,
                            configuration: { ...editFormData.configuration, exclude_paths: excludePaths }
                          });
                        }}
                        placeholder="/admin/&#10;/login/&#10;/private/"
                        multiline
                        rows={2}
                        description="Skip URLs containing these paths"
                      />
                    </Stack>

                    {/* Advanced Web Settings Section */}
                    <Stack tokens={{ childrenGap: 12 }}>
                      <Text variant="mediumPlus" style={{ fontWeight: 600, color: theme.palette.themePrimary }}>
                        🔧 Advanced Web Settings
                      </Text>
                      <Separator styles={{ root: { margin: '2px 0' } }} />
                      
                      <TextField
                        label="User Agent"
                        value={editFormData.user_agent || editFormData.configuration?.user_agent || 'BynetAI-WebCrawler/1.0'}
                        onChange={(_, value) => setEditFormData({ 
                          ...editFormData, 
                          user_agent: value || 'BynetAI-WebCrawler/1.0',
                          configuration: { ...editFormData.configuration, user_agent: value || 'BynetAI-WebCrawler/1.0' }
                        })}
                        placeholder="BynetAI-WebCrawler/1.0"
                        description="User agent string to identify the crawler"
                      />

                      <TextField
                        label="Wait Time (seconds)"
                        type="number"
                        value={(editFormData.wait_time || editFormData.configuration?.wait_time || 3).toString()}
                        onChange={(_, value) => {
                          const waitTime = value && !isNaN(parseInt(value)) ? parseInt(value) : 3;
                          setEditFormData({
                            ...editFormData,
                            wait_time: waitTime,
                            configuration: { ...editFormData.configuration, wait_time: waitTime }
                          });
                        }}
                        placeholder="3"
                        description="Seconds to wait for JavaScript content to load"
                      />
                    </Stack>

                    {/* AI Processing Section for Public Web */}
                    <Stack tokens={{ childrenGap: 12 }}>
                      <Text variant="mediumPlus" style={{ fontWeight: 600, color: theme.palette.themePrimary }}>
                        🤖 AI Processing & Indexing
                      </Text>
                      <Separator styles={{ root: { margin: '2px 0' } }} />
                      
                      <Stack horizontal tokens={{ childrenGap: 12 }} wrap>
                        <Toggle
                          label="Enable Vector Embeddings"
                          checked={editFormData.enable_embeddings ?? editFormData.configuration?.enable_embeddings ?? true}
                          onChange={(_, checked) => setEditFormData({ 
                            ...editFormData, 
                            enable_embeddings: checked || false,
                            configuration: { ...editFormData.configuration, enable_embeddings: checked || false }
                          })}
                          onText="Yes"
                          offText="No"
                        />
                        <Toggle
                          label="Enable Named Entity Recognition"
                          checked={editFormData.enable_ner ?? editFormData.configuration?.enable_ner ?? false}
                          onChange={(_, checked) => setEditFormData({ 
                            ...editFormData, 
                            enable_ner: checked || false,
                            configuration: { ...editFormData.configuration, enable_ner: checked || false }
                          })}
                          onText="Yes"
                          offText="No"
                        />
                        <Toggle
                          label="Enable Summarization"
                          checked={editFormData.enable_summarization ?? editFormData.configuration?.enable_summarization ?? false}
                          onChange={(_, checked) => setEditFormData({ 
                            ...editFormData, 
                            enable_summarization: checked || false,
                            configuration: { ...editFormData.configuration, enable_summarization: checked || false }
                          })}
                          onText="Yes"
                          offText="No"
                        />
                      </Stack>

                      {(editFormData.enable_embeddings ?? editFormData.configuration?.enable_embeddings ?? true) && (
                        <Stack tokens={{ childrenGap: 12 }}>
                          <Dropdown
                            label="Embedding Model"
                            options={[
                              { key: 'text-embedding-3-small', text: 'text-embedding-3-small (Faster, Lower Cost)' },
                              { key: 'text-embedding-3-large', text: 'text-embedding-3-large (Higher Quality)' },
                              { key: 'text-embedding-ada-002', text: 'text-embedding-ada-002 (Legacy)' }
                            ]}
                            selectedKey={editFormData.embedding_model || editFormData.configuration?.embedding_model || 'text-embedding-3-large'}
                            onChange={(_, option) => setEditFormData({ 
                              ...editFormData, 
                              embedding_model: option?.key as string || 'text-embedding-3-large',
                              configuration: { ...editFormData.configuration, embedding_model: option?.key as string || 'text-embedding-3-large' }
                            })}
                          />

                          <Stack horizontal tokens={{ childrenGap: 16 }}>
                            <TextField
                              label="Chunk Size"
                              type="number"
                              value={(editFormData.chunk_size || editFormData.configuration?.chunk_size || 1000).toString()}
                              onChange={(_, value) => {
                                const chunkSize = value && !isNaN(parseInt(value)) ? parseInt(value) : 1000;
                                setEditFormData({ 
                                  ...editFormData, 
                                  chunk_size: chunkSize,
                                  configuration: { ...editFormData.configuration, chunk_size: chunkSize }
                                });
                              }}
                              placeholder="1000"
                              description="Characters per text chunk"
                              styles={{ root: { width: '50%' } }}
                            />
                            <TextField
                              label="Chunk Overlap"
                              type="number"
                              value={(editFormData.chunk_overlap || editFormData.configuration?.chunk_overlap || 200).toString()}
                              onChange={(_, value) => {
                                const chunkOverlap = value && !isNaN(parseInt(value)) ? parseInt(value) : 200;
                                setEditFormData({ 
                                  ...editFormData, 
                                  chunk_overlap: chunkOverlap,
                                  configuration: { ...editFormData.configuration, chunk_overlap: chunkOverlap }
                                });
                              }}
                              placeholder="200"
                              description="Character overlap between chunks"
                              styles={{ root: { width: '50%' } }}
                            />
                          </Stack>
                        </Stack>
                      )}
                    </Stack>

                    {/* Index Settings Section for Public Web */}
                    <Stack tokens={{ childrenGap: 12 }}>
                      <Text variant="mediumPlus" style={{ fontWeight: 600, color: theme.palette.themePrimary }}>
                        🗂️ Index Settings
                      </Text>
                      <Separator styles={{ root: { margin: '2px 0' } }} />
                      
                      <Toggle
                        label="Create New Index"
                        checked={editFormData.create_new_index ?? editFormData.configuration?.create_new_index ?? false}
                        onChange={(_, checked) => setEditFormData({ 
                          ...editFormData, 
                          create_new_index: checked || false,
                          configuration: { ...editFormData.configuration, create_new_index: checked || false }
                        })}
                        onText="Create New"
                        offText="Use Existing"
                      />

                      {(editFormData.create_new_index ?? editFormData.configuration?.create_new_index ?? false) ? (
                        <TextField
                          label="New Index Name"
                          value={editFormData.index_name || editFormData.configuration?.index_name || ''}
                          onChange={(_, value) => {
                            setEditFormData({ 
                              ...editFormData, 
                              index_name: value || '',
                              configuration: { ...editFormData.configuration, index_name: value || '' }
                            });
                            // Validate as user types
                            if (value) {
                              const error = validateIndexName(value);
                              setIndexValidationError(error);
                            } else {
                              setIndexValidationError('Index name is required');
                            }
                          }}
                          placeholder="e.g., publicweb-my-site-index"
                          required
                          errorMessage={indexValidationError}
                          description="Name for the new search index (lowercase, no spaces, must be unique)"
                        />
                      ) : (
                        <Stack tokens={{ childrenGap: 8 }}>
                          <TextField
                            label="Existing Index Name"
                            value={editFormData.index_name || editFormData.configuration?.index_name || ''}
                            onChange={(_, value) => {
                              setEditFormData({ 
                                ...editFormData, 
                                index_name: value || '',
                                configuration: { ...editFormData.configuration, index_name: value || '' }
                              });
                              setIndexValidationError('');
                            }}
                            placeholder="Enter existing index name"
                            required
                            errorMessage={indexValidationError}
                            description="Name of the existing index to use"
                            list="available-indices"
                          />
                          {availableIndices.length > 0 && (
                            <div>
                              <Text variant="small" style={{ color: '#666', marginBottom: '4px' }}>
                                Available indices:
                              </Text>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                {availableIndices.slice(0, 10).map((indexName) => (
                                  <span
                                    key={indexName}
                                    style={{
                                      display: 'inline-block',
                                      backgroundColor: '#f3f2f1',
                                      padding: '2px 6px',
                                      borderRadius: '3px',
                                      fontSize: '11px',
                                      cursor: 'pointer',
                                      color: '#323130'
                                    }}
                                    onClick={() => {
                                      setEditFormData({ 
                                        ...editFormData, 
                                        index_name: indexName,
                                        configuration: { ...editFormData.configuration, index_name: indexName }
                                      });
                                      setIndexValidationError('');
                                    }}
                                  >
                                    {indexName}
                                  </span>
                                ))}
                                {availableIndices.length > 10 && (
                                  <span style={{ fontSize: '11px', color: '#666' }}>
                                    +{availableIndices.length - 10} more...
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </Stack>
                      )}
                    </Stack>
                  </>
                )}

                {/* Generic configuration for other connector types (excluding sharepoint and public_website) */}
                {editingJob.connectorType !== 'sharepoint_online' && 
                 editingJob.connectorType !== 'sharepoint' && 
                 editingJob.connectorType !== 'public_website' && (
                  <Stack tokens={{ childrenGap: 12 }}>
                    <Text variant="mediumPlus" style={{ fontWeight: 600, color: theme.palette.themePrimary }}>
                      ⚙️ Advanced Configuration
                    </Text>
                    <Separator styles={{ root: { margin: '2px 0' } }} />
                    
                    <Text variant="small" style={{ color: '#666' }}>
                      Advanced configuration options (JSON format):
                    </Text>
                    <TextField
                      multiline
                      rows={8}
                      value={JSON.stringify(editFormData.configuration, null, 2)}
                      onChange={(_, value) => {
                        try {
                          const parsedConfig = JSON.parse(value || '{}');
                          setEditFormData({ ...editFormData, configuration: parsedConfig });
                        } catch (e) {
                          // Keep the raw text for user to fix
                        }
                      }}
                      styles={{
                        field: { fontFamily: 'Monaco, monospace', fontSize: '12px' }
                      }}
                    />
                  </Stack>
                )}

                {/* AI Features for SharePoint On-Premises */}
                {(editingJob.connectorType === 'sharepoint_onprem' || editingJob.connectorType === 'sharepoint-onprem') && (
                  <Stack tokens={{ childrenGap: 12 }}>
                    <Text variant="mediumPlus" style={{ fontWeight: 600, color: theme.palette.themePrimary }}>
                      🧠 AI Features
                    </Text>
                    <Separator styles={{ root: { margin: '2px 0' } }} />
                    
                    <Toggle
                      label="Enable Embeddings (Vector Search)"
                      checked={editFormData.enable_embeddings !== false}
                      onChange={(_, checked) => setEditFormData({ 
                        ...editFormData, 
                        enable_embeddings: checked,
                        configuration: { ...editFormData.configuration, enable_embeddings: checked }
                      })}
                      onText="Enabled"
                      offText="Disabled"
                    />
                    <Text variant="small" style={{ color: theme.palette.neutralSecondary, marginTop: -8 }}>
                      Creates vector embeddings for semantic search capabilities
                    </Text>

                    {editFormData.enable_embeddings !== false && (
                      <Stack tokens={{ childrenGap: 4 }}>
                        <Dropdown
                          label="Embedding Model"
                          options={[
                            { key: 'text-embedding-3-small', text: 'text-embedding-3-small (Faster, Lower Cost)' },
                            { key: 'text-embedding-3-large', text: 'text-embedding-3-large (Higher Quality)' },
                            { key: 'text-embedding-ada-002', text: 'text-embedding-ada-002 (Legacy)' }
                          ]}
                          selectedKey={editFormData.embedding_model || 'text-embedding-3-large'}
                          onChange={(_, option) => setEditFormData({ 
                            ...editFormData, 
                            embedding_model: option?.key as string || 'text-embedding-3-large',
                            configuration: { ...editFormData.configuration, embedding_model: option?.key as string || 'text-embedding-3-large' }
                          })}
                        />
                        <Text variant="small" style={{ color: theme.palette.neutralSecondary }}>
                          Choose the AI model for creating document embeddings
                        </Text>
                      </Stack>
                    )}

                    {/* File Processing Options */}
                    <Text variant="medium" style={{ fontWeight: 600, color: theme.palette.themePrimary, marginTop: 8 }}>
                      📄 File Processing
                    </Text>
                    
                    <Stack tokens={{ childrenGap: 4 }}>
                      <Dropdown
                        label="File Types to Process"
                        multiSelect
                        options={fileTypeOptions}
                        selectedKeys={editFormData.file_types || ['.pdf', '.docx', '.pptx', '.xlsx', '.txt', '.html', '.msg', '.aspx']}
                        onChange={(_, option) => {
                          if (option) {
                            const currentTypes = editFormData.file_types || [];
                            const newTypes = option.selected 
                              ? [...currentTypes, option.key as string]
                              : currentTypes.filter(type => type !== option.key);
                            setEditFormData({ 
                              ...editFormData, 
                              file_types: newTypes,
                              configuration: { ...editFormData.configuration, file_types: newTypes }
                            });
                          }
                        }}
                        placeholder="Select file types to process"
                      />
                      <Text variant="small" style={{ color: theme.palette.neutralSecondary }}>
                        Only selected file types will be processed and indexed
                      </Text>
                    </Stack>

                    <TextField
                      label="Max File Size (MB)"
                      type="number"
                      value={(editFormData.max_file_size_mb || 50).toString()}
                      onChange={(_, value) => {
                        const maxSize = value && !isNaN(parseInt(value)) ? parseInt(value) : 50;
                        setEditFormData({ 
                          ...editFormData, 
                          max_file_size_mb: maxSize,
                          configuration: { ...editFormData.configuration, max_file_size_mb: maxSize }
                        });
                      }}
                      placeholder="50"
                      description="Maximum file size to process (in MB)"
                    />

                    <TextField
                      label="Batch Size"
                      type="number"
                      value={(editFormData.batch_size || 10).toString()}
                      onChange={(_, value) => {
                        const batchSize = value && !isNaN(parseInt(value)) ? parseInt(value) : 10;
                        setEditFormData({ 
                          ...editFormData, 
                          batch_size: batchSize,
                          configuration: { ...editFormData.configuration, batch_size: batchSize }
                        });
                      }}
                      placeholder="10"
                      description="Number of documents to process in each batch"
                    />

                    <Toggle
                      label="Upload-only Mode"
                      checked={editFormData.upload_only || false}
                      onChange={(_, checked) => setEditFormData({ 
                        ...editFormData, 
                        upload_only: checked || false,
                        configuration: { ...editFormData.configuration, upload_only: checked || false }
                      })}
                      onText="Upload Only"
                      offText="Full Processing"
                    />
                    
                    {editFormData.upload_only && (
                      <MessageBar messageBarType={MessageBarType.warning}>
                        <Text variant="small">
                          <strong>Upload-only Mode:</strong> Files will be crawled with metadata and ACL extraction, then uploaded to Azure Storage. 
                          No indexer or index will be created automatically - you'll need to configure skillset and indexer manually for special file processing.
                        </Text>
                      </MessageBar>
                    )}
                  </Stack>
                )}

                {/* Action Buttons */}
                <Stack horizontal tokens={{ childrenGap: 12 }} horizontalAlign="end" styles={{ root: { paddingTop: '16px', borderTop: `1px solid ${theme.palette.neutralLighter}` } }}>
                  <DefaultButton
                    text="Cancel"
                    onClick={() => {
                      setShowEditJobPanel(false);
                      setEditingJob(null);
                      setEditFormData({});
                    }}
                    styles={{
                      root: { height: '42px' },
                      label: { fontSize: '15px' }
                    }}
                  />
                  <DefaultButton
                    text="Save & Run Job"
                    onClick={async () => {
                      await handleSaveJobEdit();
                      if (editingJob) {
                        await handleRunJob(editingJob.id);
                      }
                    }}
                    disabled={editLoading || !editFormData.name || !editFormData.targetIndex}
                    iconProps={{ iconName: 'Play', styles: { root: { fontSize: '16px' } } }}
                    styles={{
                      root: { 
                        height: '42px',
                        backgroundColor: theme.palette.themePrimary,
                        borderColor: theme.palette.themePrimary,
                        color: 'white'
                      },
                      rootHovered: {
                        backgroundColor: theme.palette.themeDarkAlt,
                        borderColor: theme.palette.themeDarkAlt,
                        color: 'white'
                      },
                      label: { fontSize: '15px', fontWeight: 600 }
                    }}
                  />
                  <PrimaryButton
                    text={editLoading ? "Saving..." : "Save Changes"}
                    onClick={handleSaveJobEdit}
                    disabled={editLoading || !editFormData.name || !editFormData.targetIndex}
                    iconProps={{ iconName: 'CheckMark', styles: { root: { fontSize: '18px' } } }}
                    styles={{
                      root: { height: '42px' },
                      label: { fontSize: '15px' }
                    }}
                  />
                </Stack>
              </Stack>
            );
          })()}
        </Panel>

        {/* Delete Confirmation */}
        <Modal
          isOpen={showDeleteConfirm}
          onDismiss={() => setShowDeleteConfirm(false)}
          containerClassName={styles.deleteModal}
        >
          <Stack tokens={{ childrenGap: 20 }} style={{ padding: '24px' }}>
            <Stack tokens={{ childrenGap: 8 }}>
              <Text variant="large" style={{ fontWeight: 600 }}>
                Delete Job
              </Text>
              <Text>
                Are you sure you want to delete "{jobToDelete?.name}"? This action cannot be undone.
              </Text>
            </Stack>
            <Stack horizontal tokens={{ childrenGap: 12 }} horizontalAlign="end">
              <DefaultButton
                text="Cancel"
                onClick={() => setShowDeleteConfirm(false)}
              />
              <PrimaryButton
                text="Delete"
                onClick={async () => {
                  if (jobToDelete) {
                    await handleDeleteJob(jobToDelete.id);
                  }
                }}
                styles={{
                  root: { backgroundColor: '#a4262c' },
                  rootHovered: { backgroundColor: '#8e1f25' },
                  rootPressed: { backgroundColor: '#8e1f25' }
                }}
              />
            </Stack>
          </Stack>
        </Modal>

        {/* Bulk Delete Confirmation */}
        <Modal
          isOpen={showBulkDeleteConfirm}
          onDismiss={() => setShowBulkDeleteConfirm(false)}
          containerClassName={styles.deleteModal}
        >
          <Stack tokens={{ childrenGap: 20 }} style={{ padding: '24px' }}>
            <Stack tokens={{ childrenGap: 8 }}>
              <Text variant="large" style={{ fontWeight: 600 }}>
                Delete Multiple Jobs
              </Text>
              <Text>
                Are you sure you want to delete {selectedJobs.size} selected job{selectedJobs.size > 1 ? 's' : ''}? This action cannot be undone.
              </Text>
              {selectedJobs.size > 0 && (
                <Stack tokens={{ childrenGap: 4 }} style={{ marginTop: 12 }}>
                  <Text variant="small" style={{ fontWeight: 600 }}>Jobs to be deleted:</Text>
                  {Array.from(selectedJobs).slice(0, 5).map(jobId => {
                    const job = jobs.find(j => j.id === jobId);
                    return (
                      <Text key={jobId} variant="small" style={{ marginLeft: 8 }}>
                        • {job?.name || jobId}
                      </Text>
                    );
                  })}
                  {selectedJobs.size > 5 && (
                    <Text variant="small" style={{ marginLeft: 8, fontStyle: 'italic' }}>
                      ... and {selectedJobs.size - 5} more
                    </Text>
                  )}
                </Stack>
              )}
            </Stack>
            <Stack horizontal tokens={{ childrenGap: 12 }} horizontalAlign="end">
              <DefaultButton
                text="Cancel"
                onClick={() => setShowBulkDeleteConfirm(false)}
                disabled={bulkDeleteLoading}
              />
              <PrimaryButton
                text={bulkDeleteLoading ? 'Deleting...' : 'Delete All'}
                onClick={handleBulkDeleteJobs}
                disabled={bulkDeleteLoading}
                styles={{
                  root: { backgroundColor: '#a4262c' },
                  rootHovered: { backgroundColor: '#8e1f25' },
                  rootPressed: { backgroundColor: '#8e1f25' }
                }}
              />
            </Stack>
          </Stack>
        </Modal>
      </Stack>
    </div>
  );
};

export default JobManagement;
